import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faToggleOn, faToggleOff, faCircle,
  faUser, faArrowRightToBracket, faArrowRightFromBracket, faEye, faArrowLeft,
  faCheck, faTriangleExclamation, faBell,
  faLanguage, faWandMagicSparkles, faAngleDown, faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Modal, Box, Button, Tooltip } from '@mui/material';
import InfoTooltip from './InfoTooltip';
import { authFetch } from '../utils/api';
import { canWrite } from '../utils/permissions';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import './liveInbox.css';
import './chatHistoryOverview.css';
import './notificationCenter.css';
import JoinConversationModal from './JoinConversationModal';
import LeaveConversationModal from './LeaveConversationModal';
import PoweredByUpgradeModal from './poweredByUpgradeModal';
import WarningModal from './WarningModal';
import useUpgrade from '../hooks/useUpgrade';
import { useLiveNotifications } from './LiveNotificationsProvider';

dayjs.extend(utc);
dayjs.extend(timezone);

const getUserTimezone = () => localStorage.getItem('timezone') || 'UTC';
const API = process.env.REACT_APP_API_BASE_URL;
const WS_URL = process.env.REACT_APP_API_WEBSOCKET_URL;

// Open links in new tab (same as Website tab)
const LinkRenderer = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
);

// Inline-only markdown components for the conversation list preview.
// The preview cell uses white-space: nowrap + ellipsis, so block elements
// (p, ul, h1-h6, blockquote, …) must collapse to inline so the truncation
// keeps working and rows don't grow vertically.
const InlinePreviewComponents = {
  a: LinkRenderer,
  p: ({ children }) => <>{children} </>,
  br: () => <span> </span>,
  ul: ({ children }) => <>{children}</>,
  ol: ({ children }) => <>{children}</>,
  li: ({ children }) => <>{children} </>,
  h1: ({ children }) => <strong>{children}</strong>,
  h2: ({ children }) => <strong>{children}</strong>,
  h3: ({ children }) => <strong>{children}</strong>,
  h4: ({ children }) => <strong>{children}</strong>,
  h5: ({ children }) => <strong>{children}</strong>,
  h6: ({ children }) => <strong>{children}</strong>,
  blockquote: ({ children }) => <>{children}</>,
  pre: ({ children }) => <code>{children}</code>,
  hr: () => <span> </span>,
  img: () => null,
  table: ({ children }) => <>{children}</>,
  thead: ({ children }) => <>{children}</>,
  tbody: ({ children }) => <>{children}</>,
  tr: ({ children }) => <>{children} </>,
  th: ({ children }) => <>{children} </>,
  td: ({ children }) => <>{children} </>,
};

// Server clock minus this browser's clock, so a PC clock that is off cannot
// turn every visitor amber or red. Set from the API's Date header and from
// the server time in presence events.
let serverClockOffsetMs = 0;
const noteServerTime = (serverMs) => {
  if (Number.isFinite(serverMs)) serverClockOffsetMs = serverMs - Date.now();
};
// Backend timestamps are UTC ISO strings without a zone suffix.
const parseUtc = (iso) => new Date(/(Z|[+-]\d\d:?\d\d)$/.test(iso) ? iso : iso + 'Z').getTime();

// Task 5 — Visitor online/offline status helper. The widget beats every 15 s
// while its tab is visible: online until two beats are missed, then
// "reconnecting", then "connection lost". A tab in the background beats every
// 60 s and says so ('hidden'): "in the background" until the session would end.
const PRESENCE_ONLINE_SECONDS = 40;
const PRESENCE_LOST_SECONDS = 75;
const PRESENCE_BACKGROUND_SECONDS = 150;
const getVisitorPresenceStatus = (lastActivity, visibility) => {
  if (!lastActivity) return 'offline';
  const diffSeconds = (Date.now() + serverClockOffsetMs - parseUtc(lastActivity)) / 1000;
  if (visibility === 'hidden') return diffSeconds < PRESENCE_BACKGROUND_SECONDS ? 'background' : 'offline';
  if (diffSeconds < PRESENCE_ONLINE_SECONDS) return 'online';
  if (diffSeconds < PRESENCE_LOST_SECONDS) return 'away';
  return 'offline';
};

const mapSession = (s, getBotName) => ({
  sessionId: s.session_id,
  botId: s.bot_id,
  status: s.status,
  visitorName: s.visitor_name,
  pageUrl: s.page_url,
  lastActivity: s.last_activity,
  visibility: s.visibility || null,
  createdAt: s.created_at,
  lastMessage: s.last_message,
  lastMessageType: s.last_message_type,
  messageCount: s.message_count || 0,
  botName: getBotName(s.bot_id),
  requestedHelp: s.status === 'agent_requested',
  agentJoined: s.status === 'agent_joined',
  // Task 3 — unread tracking
  unreadCount: s.unread_count || 0,
  lastReadMessageId: s.last_read_message_id,
  country: s.country || null,
});

// Flag CDN helper — mirrors chatHistoryOverview.js so the Live Inbox shows
// a country flag next to each visitor, same as the Analytics tab.
const getCountryFlagUrl = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return null;
  return `https://flagcdn.com/32x24/${countryCode.toLowerCase()}.png`;
};

/**
 * Portal-rendered language picker menu.
 *
 * Rendered into <body> with `position: fixed` and dynamically positioned
 * relative to the trigger button so it can never be clipped by the chat
 * scroll container, the chat window, or the viewport. The menu auto-flips
 * above/below the trigger based on available space, picks the side with
 * more room, and caps its own max-height so the inner list scrolls instead
 * of overflowing the viewport.
 */
const MessageLangMenu = ({ anchor, children, onClose }) => {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!anchor) return undefined;

    const MENU_WIDTH = 200;     // matches CSS min-width / typical width
    const MIN_HEIGHT = 120;     // never shrink below this — always usable
    const MAX_HEIGHT = 260;
    const MARGIN = 8;           // gap between trigger and menu / viewport edge

    const update = () => {
      const rect = anchor.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Measure actual menu width if it has rendered, else fall back to
      // the default width estimate.
      const menuEl = menuRef.current;
      const measuredWidth = menuEl ? menuEl.offsetWidth : MENU_WIDTH;
      const width = Math.min(Math.max(measuredWidth, 160), Math.min(MENU_WIDTH + 40, vw - 2 * MARGIN));

      // Decide vertical placement: prefer the side with more space, but
      // require at least MIN_HEIGHT on that side. Cap maxHeight to the
      // available room so the menu's own scrollbar handles overflow.
      const spaceBelow = vh - rect.bottom - MARGIN;
      const spaceAbove = rect.top - MARGIN;
      let placeAbove;
      let maxHeight;
      if (spaceBelow >= MIN_HEIGHT && spaceBelow >= spaceAbove) {
        placeAbove = false;
        maxHeight = Math.min(MAX_HEIGHT, spaceBelow);
      } else if (spaceAbove >= MIN_HEIGHT) {
        placeAbove = true;
        maxHeight = Math.min(MAX_HEIGHT, spaceAbove);
      } else {
        // Neither side has comfortable room — pick the larger one and let
        // the inner list scroll within whatever we have.
        placeAbove = spaceAbove > spaceBelow;
        maxHeight = Math.max(MIN_HEIGHT, placeAbove ? spaceAbove : spaceBelow);
      }

      const top = placeAbove
        ? Math.max(MARGIN, rect.top - maxHeight - MARGIN)
        : Math.min(vh - maxHeight - MARGIN, rect.bottom + MARGIN);

      // Horizontally align the menu's right edge to the trigger's right
      // edge (matches the previous design), but clamp into the viewport.
      let left = rect.right - width;
      if (left < MARGIN) left = MARGIN;
      if (left + width > vw - MARGIN) left = vw - width - MARGIN;

      setPos({ top, left, width, maxHeight });
    };

    update();
    // Re-measure once after the menu mounts so the width clamp uses the
    // real rendered width.
    const raf = window.requestAnimationFrame(update);

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true); // capture: catch any scrolling ancestor
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchor]);

  if (!anchor || !pos) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="message-lang-menu"
      role="listbox"
      style={{
        position: 'fixed',
        top: `${pos.top}px`,
        left: `${pos.left}px`,
        width: `${pos.width}px`,
        maxHeight: `${pos.maxHeight}px`,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
};

// deepLink: { botId, sessionId } from /live?bot=..&session=.. (LiveChat page).
// LiveInbox opens `sessionId` once the session list for that bot has loaded
// and reports back via onDeepLinkConsumed so the page can drop the params.
const LiveInbox = ({ bots = [], selectedBotIds = [], onReady, deepLink = null, onDeepLinkConsumed }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { registerJoinedSession, unregisterJoinedSession, setAgentOnline } = useLiveNotifications();

  const [liveChatSettings, setLiveChatSettings] = useState({});
  const [loadingSettings, setLoadingSettings] = useState(true);

  const [onlineStatus, setOnlineStatus] = useState({});
  const [togglingStatus, setTogglingStatus] = useState({});

  // Per-bot team roster: { [botId]: [{ user_id, first_name, last_name, email, role, online, is_self }] }
  const [teamAgents, setTeamAgents] = useState({});
  // Per-(botId|userId) "forcing offline" spinner state.
  const [forcingOffline, setForcingOffline] = useState({});
  // Pending confirmation for force-offlining a teammate. When set the
  // shared WarningModal is shown to confirm the destructive action.
  const [pendingForceOffline, setPendingForceOffline] = useState(null);
  // True until the initial GET /live/agent_status call resolves.
  const [agentStatusLoading, setAgentStatusLoading] = useState(true);
  // Set of bot ids whose roster has been loaded at least once.
  const [rosterLoadedBots, setRosterLoadedBots] = useState(() => new Set());

  const [activeConversations, setActiveConversations] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  // Which bot ids the LAST completed session fetch covered — the deep-link
  // opener waits until the list actually reflects the deep-linked bot (the
  // selection may still be switching over when the inbox first loads).
  const lastFetchedBotIdsRef = useRef(null);
  // A selection change while a fetch is in flight is not lost: it re-runs
  // fetchSessions as soon as the current one settles.
  const sessionRefetchPendingRef = useRef(false);
  // Deep link (URL or in-app toast) that could not be opened because the
  // conversation is no longer active -> explain instead of silently landing
  // on the list.
  const [deepLinkNotice, setDeepLinkNotice] = useState(null);
  // Identity of the last deepLink object we handled (a fresh arrival is a
  // fresh object, see LiveChat), so a re-render never re-opens.
  const handledDeepLinkRef = useRef(null);
  // In-page toast "Open" while already on /live: queued session to open.
  const [toastOpenRequest, setToastOpenRequest] = useState(null);
  // Bounded wait for "the list covers the deep-linked bot": if the selection
  // never catches up (unexpected reconcile outcome) we stop waiting after
  // DEEP_LINK_WAIT_MS and resolve against whatever is loaded, so a deep link
  // can never hang silently. Holds the deepLink object the timer was armed for.
  const [deepLinkWaitExpiredFor, setDeepLinkWaitExpiredFor] = useState(null);
  const deepLinkWaitTimerRef = useRef(null);

  const [joinedSessionId, setJoinedSessionId] = useState(null);
  const [viewingSessionId, setViewingSessionId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [joiningSession, setJoiningSession] = useState(false);
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [joinModalSessionId, setJoinModalSessionId] = useState(null);
  const [leavingSessionId, setLeavingSessionId] = useState(null);

  // Modal shown when the visitor ends the live session (e.g. clears chat or
  // closes the widget) while the agent is viewing/joined. Stores the
  // visitor's display name so the message reads naturally.
  const [sessionEndedModal, setSessionEndedModal] = useState(null);
  // Confirmation modal shown before actually leaving a conversation.
  //   null | { sessionId: string, visitorName: string }
  const [leaveConfirm, setLeaveConfirm] = useState(null);

  // Handshake: portal is "Connecting" after clicking Join, waiting for the
  // widget to confirm the agent joined (via WS `join_confirmed`). While in
  // this state, the chat input is locked.
  //   null                 → idle
  //   { sessionId, state }  where state ∈ 'waiting' | 'timeout'
  const [connectingJoin, setConnectingJoin] = useState(null);
  const connectingTimerRef = useRef(null);
  const JOIN_CONFIRM_TIMEOUT_MS = 10000;
  const chatMessagesRef = useRef(null);
  const viewingSessionRef = useRef(null);

  // Task 1 — Visitor typing state
  const [visitorTyping, setVisitorTyping] = useState({});
  const visitorTypingTimerRef = useRef({});
  const agentTypingTimerRef = useRef(null);
  const agentIsTypingRef = useRef(false);

  // ── Agent assist: per-message translations + per-session target language ──
  // messageTranslations:  { [sessionId]: { [messageId]: { state, text, lang } } }
  //   state ∈ 'loading' | 'shown' | 'hidden' | 'error'
  // sessionTargetLang:    { [sessionId]: string }   — agent's preferred language
  const [messageTranslations, setMessageTranslations] = useState({});
  // messageLang: { [sessionId]: { [messageId]: 'en' | 'de' | ... } } — per-message picker
  const [messageLang, setMessageLang] = useState({});
  // langPickerOpen: id of the message whose flag-picker is currently open ('sid:msgId') | null
  const [langPickerOpen, setLangPickerOpen] = useState(null);
  // DOM element of the currently-open trigger button (used to position the
  // portal-rendered menu so it can never be clipped by the chat scroll area).
  const [langPickerAnchor, setLangPickerAnchor] = useState(null);
  const [sessionTargetLang, setSessionTargetLang] = useState({});
  // Input-area translate picker (next to the send button) — true when open.
  const [inputLangPickerOpen, setInputLangPickerOpen] = useState(false);
  // True while the agent's reply text is being translated in-place.
  const [inputTranslating, setInputTranslating] = useState(false);

  // ── Agent assist: AI-suggested replies ──
  const [suggesting, setSuggesting] = useState(false);
  const [creditsLeft, setCreditsLeft] = useState({}); // { [botId]: number }
  // Abort controllers for in-flight assist requests so the agent can cancel.
  const suggestAbortRef = useRef(null);
  const translateAbortRef = useRef(null);

  // ── Plan gating for agent-assist features ──
  // Suggest requires Boost+ (Boost or Ultimo).
  // Translate requires Smart+ (Smart, Boost, or Ultimo).
  // When the active bot's plan is below the required tier, clicking the
  // feature button opens an upgrade modal (same pattern as the rescraping
  // setting in knowledgeOverview.js) instead of calling the backend.
  const [assistUpgradeModal, setAssistUpgradeModal] = useState(null); // { bot, title, description, requiredPlan } | null
  const { handleUpgradeClick: upgradeClick, UpgradeModal: AssistSubscriptionModal } = useUpgrade({ onComplete: () => {} });

  // Refs for stable access inside WS handler
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const joinedSessionRef = useRef(null);
  const connectingSessionRef = useRef(null);
  const getBotNameRef = useRef(null);
  const activeConversationsRef = useRef([]);

  // DB-first polling refs
  const lastSeenMsgIdRef = useRef(0);
  const sessionFetchInFlightRef = useRef(false);
  const msgFetchInFlightRef = useRef(false);
  const fetchJoinedMessagesRef = useRef(null);
  const fetchSessionsNowRef = useRef(null);
  const sendVisitorMsgAckRef = useRef(null);

  // Task 4 — setTimeout-chain poll refs
  const sessionPollTimerRef = useRef(null);
  const msgPollTimerRef = useRef(null);

  // P1 — WS-authoritative polling (HTTP as fallback)
  const wsHealthRef = useRef({ lastEventAt: 0, connected: false });
  // P4 — subscription diffing: track which bot_ids we've told the server about
  const subscribedBotIdsRef = useRef([]);
  const pendingSubsRef = useRef({ add: new Set(), remove: new Set() });
  const selectedBotIdsRef = useRef([]);
  useEffect(() => { selectedBotIdsRef.current = selectedBotIds; }, [selectedBotIds]);
  // P5 — exp backoff reconnect
  const attemptRef = useRef(0);
  const reconnectNowRef = useRef(null);
  const lastSessionsFetchAtRef = useRef(0);
  const sessionsRefreshTimerRef = useRef(null);
  // P5/P6 — terminal close (4401) flag so we don't reconnect
  const terminalAuthFailureRef = useRef(false);

  const getBotName = useCallback(
    (botId) => bots.find(b => b.id === botId)?.name || 'Bot',
    [bots]
  );

  // Keep refs in sync
  useEffect(() => { joinedSessionRef.current = joinedSessionId; }, [joinedSessionId]);
  useEffect(() => { viewingSessionRef.current = viewingSessionId; }, [viewingSessionId]);
  useEffect(() => { connectingSessionRef.current = connectingJoin ? connectingJoin.sessionId : null; }, [connectingJoin]);
  useEffect(() => { getBotNameRef.current = getBotName; }, [getBotName]);
  useEffect(() => { activeConversationsRef.current = activeConversations; }, [activeConversations]);

  // Register the joined session with the app-wide notifications provider so
  // it fires "new visitor message" toasts even when the agent has navigated
  // away from /live. The provider also reconciles joinedSessionsRef from the
  // batch sessions endpoint on an interval (P3), so unregistering on unmount
  // is safe — the next reconciliation will restore the entry if the backend
  // still reports us as agent_joined.
  useEffect(() => {
    const sid = joinedSessionId || (connectingJoin && connectingJoin.sessionId);
    if (!sid) return;
    registerJoinedSession(sid);
    return () => {
      unregisterJoinedSession(sid);
    };
  }, [joinedSessionId, connectingJoin, registerJoinedSession, unregisterJoinedSession]);

  // -- Fetch live chat settings --
  // Batch endpoint, 50 bots per request (a workspace can have thousands).
  const fetchLiveChatSettings = useCallback(async () => {
    setLoadingSettings(true);
    const settings = {};
    try {
      const chunks = [];
      for (let i = 0; i < selectedBotIds.length; i += 50) chunks.push(selectedBotIds.slice(i, i + 50));
      const results = await Promise.all(chunks.map(async (chunk) => {
        try {
          const res = await authFetch(`${API}live_chat_settings?bot_ids=${encodeURIComponent(chunk.join(','))}`);
          if (res && res.ok) return await res.json();
        } catch (err) {
          console.error('Error fetching live chat settings:', err);
        }
        return null;
      }));
      for (const byBot of results) {
        if (byBot && typeof byBot === 'object') Object.assign(settings, byBot);
      }
    } catch (err) {
      console.error('Error fetching live chat settings:', err);
    }
    setLiveChatSettings(settings);
    setLoadingSettings(false);
  }, [selectedBotIds]);

  useEffect(() => {
    if (selectedBotIds.length > 0) {
      fetchLiveChatSettings();
    } else {
      setLiveChatSettings({});
      setLoadingSettings(false);
    }
  }, [fetchLiveChatSettings, selectedBotIds]);

  // -- Fetch initial agent online/offline status from backend --
  useEffect(() => {
    const fetchAgentStatus = async () => {
      try {
        const res = await authFetch(`${API}live/agent_status`);
        if (res && res.ok) {
          const data = await res.json();
          const statuses = data.statuses || {};
          setOnlineStatus(statuses);
          // P7 — seed heartbeat for bots already marked online so the server
          // doesn't reap them to offline after 90s.
          for (const [botId, online] of Object.entries(statuses)) {
            if (online) {
              const parsedId = isNaN(Number(botId)) ? botId : Number(botId);
              setAgentOnline(parsedId, true);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching agent status:', err);
      } finally {
        setAgentStatusLoading(false);
      }
    };
    fetchAgentStatus();
  }, [setAgentOnline]);

  // -- Fetch + poll team roster (presence per bot for every workspace
  // teammate). Visible to everyone (including read-only). 30s cadence
  // matches the heartbeat interval so the dot can never be more than
  // ~30s stale.
  const fetchTeamAgents = useCallback(async (botIds) => {
    const ids = (botIds || []).filter(Boolean);
    if (ids.length === 0) return;
    try {
      const chunks = [];
      for (let i = 0; i < ids.length; i += 50) chunks.push(ids.slice(i, i + 50));
      const entries = (await Promise.all(chunks.map(async (chunk) => {
        try {
          const res = await authFetch(`${API}live/agents?bot_ids=${encodeURIComponent(chunk.join(','))}`);
          if (res && res.ok) {
            const data = await res.json();
            return chunk.map((botId) => [botId, Array.isArray(data[botId]) ? data[botId] : []]);
          }
        } catch (_) { /* swallow */ }
        return chunk.map((botId) => [botId, []]);
      }))).flat();
      setTeamAgents((prev) => {
        const next = { ...prev };
        for (const [botId, agents] of entries) next[botId] = agents;
        return next;
      });
      for (const [botId, agents] of entries) {
        const self = agents.find((agent) => agent.is_self);
        if (self) {
          setOnlineStatus((prev) => ({ ...prev, [botId]: self.online }));
          const parsedId = isNaN(Number(botId)) ? botId : Number(botId);
          setAgentOnline(parsedId, self.online);
        }
      }
      setRosterLoadedBots((prev) => {
        const next = new Set(prev);
        for (const [botId] of entries) next.add(botId);
        return next;
      });
      setRosterLoadedBots((prev) => {
        const next = new Set(prev);
        for (const [botId] of entries) next.add(botId);
        return next;
      });
    } catch (_) { /* swallow */ }
  }, [setAgentOnline]);

  useEffect(() => {
    if (!selectedBotIds || selectedBotIds.length === 0) {
      setTeamAgents({});
      return undefined;
    }
    fetchTeamAgents(selectedBotIds);
    const timer = setInterval(() => fetchTeamAgents(selectedBotIds), 30000);
    return () => clearInterval(timer);
  }, [selectedBotIds, fetchTeamAgents]);

  const handleForceTeammateOffline = useCallback(async (botId, userId) => {
    if (!canWrite()) return; // backend will also enforce
    const key = `${botId}|${userId}`;
    setForcingOffline((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await authFetch(
        `${API}live/agent_status/${botId}/user/${userId}`,
        { method: 'PUT', body: JSON.stringify({ status: 'offline' }) }
      );
      if (res && res.ok) {
        setTeamAgents((prev) => {
          const list = prev[botId] || [];
          return {
            ...prev,
            [botId]: list.map((a) => a.user_id === userId ? { ...a, online: false } : a),
          };
        });
      }
    } catch (_) { /* swallow */ }
    setForcingOffline((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // -- Fetch active sessions from backend (DB source of truth) --
  // P2.1: Batch endpoint — GET /live/sessions?bot_ids=a,b,c returns
  // { "<bot_id>": [session, ...], ... }. Cap 50 ids per call, so chunk.
  const fetchSessions = useCallback(async () => {
    if (sessionFetchInFlightRef.current) {
      sessionRefetchPendingRef.current = true;
      return;
    }
    if (!selectedBotIds || selectedBotIds.length === 0) {
      setActiveConversations([]);
      // Only mark sessions "loaded" once we know the user has no bots at
      // all. While the parent is still resolving its bot list (bots empty
      // too), keep the loading state so the inbox shows a spinner instead
      // of flashing the "No active conversations" empty state.
      if (!bots || bots.length === 0) {
        setLoadingSessions(false);
      }
      return;
    }
    sessionFetchInFlightRef.current = true;
    lastSessionsFetchAtRef.current = Date.now();
    try {
      const CHUNK = 50;
      const chunks = [];
      for (let i = 0; i < selectedBotIds.length; i += CHUNK) {
        chunks.push(selectedBotIds.slice(i, i + CHUNK));
      }
      const chunkResults = await Promise.all(chunks.map(async (ids) => {
        const qs = encodeURIComponent(ids.join(','));
        const res = await authFetch(`${API}live/sessions?bot_ids=${qs}`);
        if (!res || !res.ok) return { ids, mapByBot: null };
        const serverDate = res.headers && res.headers.get('Date');
        if (serverDate) noteServerTime(new Date(serverDate).getTime());
        try { return { ids, mapByBot: await res.json() }; } catch { return { ids, mapByBot: null }; }
      }));
      const allSessions = [];
      const failedBotIds = new Set();
      for (const { ids, mapByBot } of chunkResults) {
        if (!mapByBot || typeof mapByBot !== 'object') {
          ids.forEach((id) => failedBotIds.add(String(id)));
          continue;
        }
        for (const botId of Object.keys(mapByBot)) {
          const arr = mapByBot[botId];
          if (!Array.isArray(arr)) continue;
          for (const s of arr) {
            allSessions.push(mapSession(s, getBotName));
          }
        }
      }
      // A chunk that failed (rate limit, network) keeps its last known
      // sessions instead of emptying the inbox until the next poll.
      for (const c of activeConversationsRef.current) {
        if (failedBotIds.has(String(c.botId))) allSessions.push(c);
      }
      // Stable ordering: sort by createdAt ascending so the oldest session
      // keeps #1 and new sessions are appended with higher numbers.
      // Tie-break by sessionId for full determinism.
      allSessions.sort((a, b) => {
        const diff = new Date(a.createdAt) - new Date(b.createdAt);
        if (diff !== 0) return diff;
        return (a.sessionId || '').localeCompare(b.sessionId || '');
      });
      setActiveConversations(allSessions);
      lastFetchedBotIdsRef.current = selectedBotIds.map(String);
    } catch (err) {
      console.error('Error fetching live sessions:', err);
    } finally {
      sessionFetchInFlightRef.current = false;
      setLoadingSessions(false);
      if (sessionRefetchPendingRef.current) {
        sessionRefetchPendingRef.current = false;
        fetchSessionsNowRef.current?.();
      }
    }
  }, [selectedBotIds, getBotName, bots]);

  // Country resolution — for any live session we show that doesn't yet have
  // a cached country, hit `/api/live/resolve_country/{id}`. The backend
  // geolocates the visitor IP (ip-api.com) and persists the result to
  // chat_history so subsequent list polls come back pre-populated. Until
  // the first resolution completes we render a spinner in place of the flag.
  const countryResolvingRef = useRef(new Set());
  const [countryUnresolved, setCountryUnresolved] = useState(() => new Set());

  const resolveCountry = useCallback(async (sessionId) => {
    if (!sessionId) return;
    if (countryResolvingRef.current.has(sessionId)) return;
    countryResolvingRef.current.add(sessionId);
    try {
      const res = await authFetch(`${API}live/resolve_country/${sessionId}`);
      if (!res || !res.ok) {
        setCountryUnresolved(prev => {
          if (prev.has(sessionId)) return prev;
          const next = new Set(prev); next.add(sessionId); return next;
        });
        return;
      }
      const data = await res.json();
      if (data && data.country) {
        setActiveConversations(prev => prev.map(c =>
          c.sessionId === sessionId ? { ...c, country: data.country } : c
        ));
      } else {
        // No public IP or lookup failed — don't retry on every poll, and
        // stop showing a spinner for this session.
        setCountryUnresolved(prev => {
          if (prev.has(sessionId)) return prev;
          const next = new Set(prev); next.add(sessionId); return next;
        });
      }
    } catch (_err) {
      setCountryUnresolved(prev => {
        if (prev.has(sessionId)) return prev;
        const next = new Set(prev); next.add(sessionId); return next;
      });
    } finally {
      countryResolvingRef.current.delete(sessionId);
    }
  }, []);

  useEffect(() => {
    activeConversations.forEach(c => {
      if (
        !c.country &&
        !countryResolvingRef.current.has(c.sessionId) &&
        !countryUnresolved.has(c.sessionId)
      ) {
        resolveCountry(c.sessionId);
      }
    });
  }, [activeConversations, countryUnresolved, resolveCountry]);

  // -- DB-first: fetch new messages for joined session --
  const fetchJoinedMessages = useCallback(async () => {
    if (msgFetchInFlightRef.current) return;
    const sessionId = joinedSessionRef.current || connectingSessionRef.current || viewingSessionRef.current;
    if (!sessionId) return;
    msgFetchInFlightRef.current = true;
    try {
      const res = await authFetch(
        `${API}live/messages/${sessionId}?after_id=${lastSeenMsgIdRef.current}`
      );
      if (!res || !res.ok) return;
      const data = await res.json();

      const newMsgs = data.messages || [];
      if (newMsgs.length > 0) {
        // Update high-water mark
        for (const msg of newMsgs) {
          if (msg.id > lastSeenMsgIdRef.current) {
            lastSeenMsgIdRef.current = msg.id;
          }
        }

        // Handshake: ack visitor messages we just received so the widget can
        // render ✓ Delivered. Only ack messages that are not yet marked
        // delivered in the DB (to avoid redundant ws traffic on reloads).
        const unackedVisitorIds = newMsgs
          .filter(m => m.type === 'user_input' && !m.delivered_at && typeof m.id === 'number')
          .map(m => m.id);
        if (unackedVisitorIds.length > 0) {
          sendVisitorMsgAckRef.current?.(sessionId, unackedVisitorIds);
        }

        setChatMessages(prev => {
          // Collect DB IDs already in state
          const existingDbIds = new Set();
          for (const m of prev) {
            if (typeof m.id === 'number' && m.id > 0 && m.id < 1e12) {
              existingDbIds.add(m.id);
            }
          }

          let updated = [...prev];

          for (const msg of newMsgs) {
            // Skip if already present by DB id
            if (existingDbIds.has(msg.id)) continue;

            // Reconcile optimistic agent messages (sent by us, already in state with temp id)
            if (msg.type === 'agent') {
              // P2.1: Require tempId on the optimistic message to avoid false content matches
              const optimisticIdx = updated.findIndex(
                m => m.type === 'agent'
                  && m.tempId  // must be an optimistic message
                  && m.content === msg.content
                  && (typeof m.id !== 'number' || m.id >= 1e12)
              );
              if (optimisticIdx >= 0) {
                // Replace temp message with authoritative DB version
                updated[optimisticIdx] = { ...msg };
                continue;
              }
            }

            // Genuinely new message — append
            updated.push(msg);
          }

          return updated;
        });
      }

      // Update session status if changed
      if (data.status) {
        setActiveConversations(prev =>
          prev.map(c => c.sessionId === sessionId
            ? {
                ...c,
                status: data.status,
                agentJoined: data.status === 'agent_joined',
                requestedHelp: data.status === 'agent_requested',
              }
            : c
          )
        );
      }

      // P6.2: Auto-mark as read when we're actively viewing this session
      if (newMsgs.length > 0) {
        authFetch(`${API}live/read/${sessionId}`, { method: 'POST' }).catch(() => {});
        setActiveConversations(prev =>
          prev.map(c => c.sessionId === sessionId ? { ...c, unreadCount: 0 } : c)
        );
      }
    } catch (err) {
      console.error('Message poll error:', err);
    } finally {
      msgFetchInFlightRef.current = false;
    }
  }, []);

  // Event-driven session refreshes: at most one per interval that grows with
  // the number of 50-bot chunks, so a workspace with thousands of bots does
  // not reload every chunk on each visitor message across all its bots.
  const requestSessionsRefresh = useCallback(() => {
    if (sessionsRefreshTimerRef.current) return;
    const chunkCount = Math.max(1, Math.ceil((selectedBotIdsRef.current || []).length / 50));
    const gap = Math.max(2000, chunkCount * 500);
    const wait = Math.max(0, lastSessionsFetchAtRef.current + gap - Date.now());
    sessionsRefreshTimerRef.current = setTimeout(() => {
      sessionsRefreshTimerRef.current = null;
      fetchSessionsNowRef.current?.();
    }, wait);
  }, []);
  useEffect(() => () => clearTimeout(sessionsRefreshTimerRef.current), []);

  // Keep fetch refs in sync
  useEffect(() => { fetchJoinedMessagesRef.current = fetchJoinedMessages; }, [fetchJoinedMessages]);
  useEffect(() => { fetchSessionsNowRef.current = fetchSessions; }, [fetchSessions]);

  // P1 — WS-authoritative polling: demote poll when WS is healthy
  // connected && events within last 60s → 45s, otherwise → fast fallback
  const sessionPollInterval = useCallback(() => {
    const { connected, lastEventAt } = wsHealthRef.current;
    const stale = (Date.now() - (lastEventAt || 0)) >= 60000;
    return (connected && !stale) ? 45000 : 5000;
  }, []);

  const messagePollInterval = useCallback(() => {
    const { connected, lastEventAt } = wsHealthRef.current;
    const stale = (Date.now() - (lastEventAt || 0)) >= 60000;
    return (connected && !stale) ? 45000 : 2000;
  }, []);

  // Task 4 — Schedule helpers: push back poll timers when WS fires
  const scheduleSessionPoll = useCallback(() => {
    clearTimeout(sessionPollTimerRef.current);
    sessionPollTimerRef.current = setTimeout(() => {
      fetchSessionsNowRef.current?.();
      scheduleSessionPoll();
    }, sessionPollInterval());
  }, [sessionPollInterval]);

  const scheduleMsgPoll = useCallback(() => {
    clearTimeout(msgPollTimerRef.current);
    if (!joinedSessionRef.current && !viewingSessionRef.current && !connectingSessionRef.current) return;
    msgPollTimerRef.current = setTimeout(() => {
      fetchJoinedMessagesRef.current?.();
      scheduleMsgPoll();
    }, messagePollInterval());
  }, [messagePollInterval]);

  // Handshake helper: ack visitor messages via WS (portal → backend → widget)
  const sendVisitorMsgAck = useCallback((sessionId, messageIds) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (!sessionId || !Array.isArray(messageIds) || messageIds.length === 0) return;
    try {
      ws.send(JSON.stringify({
        type: 'visitor_msg_ack',
        session_id: sessionId,
        message_ids: messageIds,
      }));
    } catch (err) {
      // best-effort
    }
  }, []);
  useEffect(() => { sendVisitorMsgAckRef.current = sendVisitorMsgAck; }, [sendVisitorMsgAck]);

  // -- WS event handler: triggers DB fetches instead of mutating state directly --
  const handleWsEvent = useCallback((data) => {
    switch (data.type) {
      // Task 1 — Visitor typing events
      case 'typing_start': {
        const sid = data.session_id;
        if (!sid) break;
        setVisitorTyping(prev => ({ ...prev, [sid]: true }));
        // Clear any existing safety timer
        if (visitorTypingTimerRef.current[sid]) {
          clearTimeout(visitorTypingTimerRef.current[sid]);
        }
        // Safety fallback: auto-clear after 30s in case typing_stop is never received
        // L2: also delete the map entry so visitorTypingTimerRef doesn't grow unbounded
        visitorTypingTimerRef.current[sid] = setTimeout(() => {
          setVisitorTyping(prev => ({ ...prev, [sid]: false }));
          delete visitorTypingTimerRef.current[sid];
        }, 30000);
        break;
      }
      case 'typing_stop': {
        const sid = data.session_id;
        if (!sid) break;
        setVisitorTyping(prev => ({ ...prev, [sid]: false }));
        if (visitorTypingTimerRef.current[sid]) {
          clearTimeout(visitorTypingTimerRef.current[sid]);
          delete visitorTypingTimerRef.current[sid];
        }
        break;
      }
      // Task 2 — Message delivery status events
      case 'message_delivered': {
        const deliveredIds = Array.isArray(data.message_ids) ? data.message_ids : null;
        const direction = data.direction; // 'agent' | 'visitor' | undefined
        setChatMessages(prev =>
          prev.map(m => {
            if (m.delivered_at) return m;
            // Filter by direction when provided
            if (direction === 'agent' && m.type !== 'agent') return m;
            if (direction === 'visitor' && m.type !== 'user_input') return m;
            if (!direction && m.type !== 'agent') return m; // legacy: agent-only
            // Filter by ids when provided
            if (deliveredIds && (typeof m.id !== 'number' || !deliveredIds.includes(m.id))) return m;
            return { ...m, delivered_at: data.delivered_at || new Date().toISOString() };
          })
        );
        // Also push back session poll
        scheduleSessionPoll();
        break;
      }
      case 'join_confirmed': {
        // Handshake: widget confirmed the agent is visible in the chat UI.
        // Unblock the portal's "Connecting..." state.
        const confirmedSid = data.session_id;
        if (!confirmedSid) break;
        setConnectingJoin(prev => {
          if (!prev || prev.sessionId !== confirmedSid) return prev;
          clearTimeout(connectingTimerRef.current);
          connectingTimerRef.current = null;
          // Promote to full joined state
          setJoinedSessionId(confirmedSid);
          return null;
        });
        fetchSessionsNowRef.current?.();
        fetchJoinedMessagesRef.current?.();
        break;
      }
      case 'messages_read': {
        const sid = data.session_id;
        setChatMessages(prev =>
          prev.map(m =>
            m.type === 'agent' && !m.read_at
              ? { ...m, read_at: data.read_at || new Date().toISOString() }
              : m
          )
        );
        scheduleSessionPoll();
        break;
      }
      case 'live_presence': {
        // Sent on every visitor heartbeat: move the presence dot, nothing else.
        if (data.server_now) noteServerTime(parseUtc(data.server_now));
        const sid = data.session_id;
        const lastActivity = data.last_activity;
        if (!sid || !lastActivity) break;
        setActiveConversations(prev => {
          let changed = false;
          const next = prev.map(c => {
            // Widgets before 2026-10-21 send no visibility: keep what we know.
            const visibility = data.visibility || c.visibility || null;
            if (c.sessionId !== sid || (c.lastActivity === lastActivity && c.visibility === visibility)) return c;
            changed = true;
            return { ...c, lastActivity, visibility };
          });
          return changed ? next : prev;
        });
        break;
      }
      case 'live_heartbeat': {
        // Status, name or page changed: patch a known session in place; an
        // unknown one (new or revived) needs the list from the DB.
        const s = data.session;
        const known = s && s.status !== 'ended'
          && activeConversationsRef.current.some(c => c.sessionId === s.session_id);
        if (known) {
          setActiveConversations(prev => prev.map(c => (c.sessionId !== s.session_id ? c : {
            ...c,
            status: s.status,
            visitorName: s.visitor_name,
            pageUrl: s.page_url,
            lastActivity: s.last_activity || c.lastActivity,
            requestedHelp: s.status === 'agent_requested',
            agentJoined: s.status === 'agent_joined',
          })));
        } else {
          requestSessionsRefresh();
        }
        scheduleSessionPoll();
        break;
      }
      case 'live_session_created':
      case 'live_agent_requested':
      case 'live_agent_joined':
      case 'live_request_cancelled':
        // Session state changed — refresh from DB, push back poll
        requestSessionsRefresh();
        scheduleSessionPoll();
        break;
      case 'live_agent_left': {
        // Always refresh sessions/poll
        requestSessionsRefresh();
        scheduleSessionPoll();
        // If the visitor initiated the disconnect (clicked "Disconnect Agent"
        // in the widget) and we're currently joined/viewing/connecting to
        // that session, surface the same modal we show for `live_session_ended`
        // so the agent isn't surprised when the chat input suddenly locks.
        if (data.initiated_by === 'visitor') {
          const leftSid = data.session_id;
          if (leftSid && (
            joinedSessionRef.current === leftSid ||
            viewingSessionRef.current === leftSid ||
            connectingSessionRef.current === leftSid
          )) {
            const conv = activeConversationsRef.current.find(c => c.sessionId === leftSid);
            const visitorName = conv?.visitorName || '';
            clearTimeout(connectingTimerRef.current);
            connectingTimerRef.current = null;
            setConnectingJoin(null);
            setJoinedSessionId(null);
            setViewingSessionId(null);
            setChatMessages([]);
            lastSeenMsgIdRef.current = 0;
            setReplyText('');
            setSessionEndedModal({ visitorName, reason: 'disconnected' });
          }
        }
        break;
      }
      case 'live_session_ended': {
        // Session was ended (visitor cleared the chat, or left the site:
        // reason 'timeout') — refresh sessions
        requestSessionsRefresh();
        scheduleSessionPoll();
        // If we're viewing/joined/connecting to this session, leave it AND
        // surface a modal so the agent isn't surprised when the chat just
        // disappears from under them.
        const endedSid = data.session_id;
        if (endedSid && (
          joinedSessionRef.current === endedSid ||
          viewingSessionRef.current === endedSid ||
          connectingSessionRef.current === endedSid
        )) {
          const conv = activeConversationsRef.current.find(c => c.sessionId === endedSid);
          const visitorName = conv?.visitorName || '';
          clearTimeout(connectingTimerRef.current);
          connectingTimerRef.current = null;
          setConnectingJoin(null);
          setJoinedSessionId(null);
          setViewingSessionId(null);
          setChatMessages([]);
          lastSeenMsgIdRef.current = 0;
          setReplyText('');
          setSessionEndedModal({ visitorName });
        }
        break;
      }
      case 'live_visitor_message':
      case 'live_assistant_message':
        // New message in a session — refresh sessions and messages from DB, push back polls
        requestSessionsRefresh();
        // Only the open conversation's messages need a refetch.
        if (!data.session_id
          || data.session_id === joinedSessionRef.current
          || data.session_id === viewingSessionRef.current
          || data.session_id === connectingSessionRef.current) {
          fetchJoinedMessagesRef.current?.();
        }
        scheduleSessionPoll();
        scheduleMsgPoll();
        break;
      case 'live_agent_message': {
        // Refresh session list (for lastMessage update)
        requestSessionsRefresh();
        scheduleSessionPoll();
        // For messages from OTHER agents, trigger message fetch
        const myUserId = parseInt(localStorage.getItem('user_id'));
        if (data.sender_user_id !== myUserId) {
          fetchJoinedMessagesRef.current?.();
          scheduleMsgPoll();
        }
        break;
      }
      default:
        break;
    }
  }, [scheduleSessionPoll, scheduleMsgPoll, requestSessionsRefresh]);

  // -- WebSocket setup (P4: lifecycle decoupled from selectedBotIds) --
  // Effect A owns the socket: connect/reconnect, onopen/onmessage/onclose/onerror.
  // Effect B (below) diffs selectedBotIds and sends live_subscribe/live_unsubscribe
  // deltas on the open socket without tearing it down.
  const userIdForWs = localStorage.getItem('user_id');
  useEffect(() => {
    if (!userIdForWs) return;

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      // P6 — guard against opening a WS without an auth token
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        console.warn('LiveInbox WS: no auth token available, skipping connect');
        return;
      }
      if (terminalAuthFailureRef.current) {
        console.warn('LiveInbox WS: prior 4401 close, not reconnecting');
        return;
      }
      // Replace any previous socket, also one still connecting; its late
      // events are ignored below (wsRef.current !== ws).
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* noop */ }
      }

      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        if (wsRef.current !== ws) return;
        // P1 — mark WS healthy
        wsHealthRef.current.connected = true;
        wsHealthRef.current.lastEventAt = Date.now();
        // P5 — reset exp backoff attempt counter
        attemptRef.current = 0;
        try {
          // P6 — init now REQUIRES the JWT
          ws.send(JSON.stringify({ type: 'init', user_id: userIdForWs, token: authToken }));
          // P4 — subscribe to the CURRENT selection; additive contract on server
          const ids = (selectedBotIdsRef.current || []).slice();
          if (ids.length > 0) {
            ws.send(JSON.stringify({ type: 'live_subscribe', bot_ids: ids }));
          }
          subscribedBotIdsRef.current = ids;
          // Flush any queued subscribe/unsubscribe deltas accumulated while
          // the socket was down.
          const pendingAdd = Array.from(pendingSubsRef.current.add);
          const pendingRemove = Array.from(pendingSubsRef.current.remove);
          if (pendingAdd.length > 0) {
            // Don't double-subscribe ids already in ids
            const extra = pendingAdd.filter((b) => !ids.includes(b));
            if (extra.length > 0) {
              ws.send(JSON.stringify({ type: 'live_subscribe', bot_ids: extra }));
              subscribedBotIdsRef.current = [...subscribedBotIdsRef.current, ...extra];
            }
          }
          if (pendingRemove.length > 0) {
            ws.send(JSON.stringify({ type: 'live_unsubscribe', bot_ids: pendingRemove }));
            subscribedBotIdsRef.current = subscribedBotIdsRef.current.filter(
              (b) => !pendingRemove.includes(b)
            );
          }
          pendingSubsRef.current = { add: new Set(), remove: new Set() };
        } catch (err) {
          console.error('LiveInbox WS open send error:', err);
        }
        // P1 — catch-up fetch on reconnect to get anything missed during disconnect
        fetchSessionsNowRef.current?.();
        fetchJoinedMessagesRef.current?.();
        // Re-schedule polls so they pick up the new (longer) healthy interval
        scheduleSessionPoll();
        scheduleMsgPoll();
      };

      ws.onmessage = (event) => {
        if (wsRef.current !== ws) return;
        // P1 — any inbound message means the WS is alive
        wsHealthRef.current.lastEventAt = Date.now();
        wsHealthRef.current.connected = true;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong' }));
            return;
          }
          if (data.type === 'auth_error') {
            // P6 — auth failed; expect a close with code 4401 to follow
            console.error('LiveInbox WS auth_error:', data.reason || '(no reason)');
            terminalAuthFailureRef.current = true;
            try { ws.close(); } catch { /* noop */ }
            return;
          }
          handleWsEvent(data);
        } catch (err) {
          console.error('LiveInbox WS parse error:', err);
        }
      };

      ws.onclose = (evt) => {
        // A replaced socket closing must not schedule another reconnect.
        if (wsRef.current !== ws) return;
        wsHealthRef.current.connected = false;
        // P6/P5 — 4401 is a terminal auth failure; do not reconnect
        if (evt && evt.code === 4401) {
          terminalAuthFailureRef.current = true;
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
          console.error('LiveInbox WS closed with 4401 (auth failure). Not reconnecting.');
          return;
        }
        if (cancelled) return;
        if (terminalAuthFailureRef.current) return;
        // P5 — exponential backoff with ±50% jitter, capped at 30s
        const attempt = attemptRef.current || 0;
        const base = Math.min(30000, 1000 * Math.pow(2, attempt));
        const delay = Math.floor(base * (0.5 + Math.random()));
        attemptRef.current = attempt + 1;
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(connect, delay);
        // Demote polls since WS just went down
        scheduleSessionPoll();
        scheduleMsgPoll();
      };

      ws.onerror = () => {
        if (wsRef.current === ws) wsHealthRef.current.connected = false;
        try { ws.close(); } catch { /* noop */ }
      };
    };

    connect();
    reconnectNowRef.current = () => {
      if (cancelled || terminalAuthFailureRef.current) return;
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
      attemptRef.current = 0;
      connect();
    };

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on intentional close
        try { wsRef.current.close(); } catch { /* noop */ }
        wsRef.current = null;
      }
      wsHealthRef.current.connected = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdForWs, handleWsEvent]);

  // Back from sleep, a hidden tab or a network change: catch up at once and
  // replace a socket that died silently. The server pings an idle socket
  // every 60 s, so 75 s without any message means it is gone.
  useEffect(() => {
    const WS_QUIET_LIMIT_MS = 75000;
    const isDead = () => {
      const ws = wsRef.current;
      const quietMs = Date.now() - (wsHealthRef.current.lastEventAt || 0);
      return !ws || ws.readyState === WebSocket.CLOSED || (ws.readyState === WebSocket.OPEN && quietMs > WS_QUIET_LIMIT_MS);
    };
    const revive = () => {
      if (document.visibilityState === 'hidden') return;
      fetchSessionsNowRef.current?.();
      fetchJoinedMessagesRef.current?.();
      if (isDead()) reconnectNowRef.current?.();
    };
    const watchdog = setInterval(() => {
      if (wsRef.current && isDead()) reconnectNowRef.current?.();
    }, 15000);
    document.addEventListener('visibilitychange', revive);
    window.addEventListener('online', revive);
    return () => {
      clearInterval(watchdog);
      document.removeEventListener('visibilitychange', revive);
      window.removeEventListener('online', revive);
    };
  }, []);

  // The presence dots age without new events: re-render every 5 s while
  // there are conversations.
  const [, setPresenceTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      if (activeConversationsRef.current.length > 0) setPresenceTick((n) => n + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Effect B — diff selectedBotIds vs subscribedBotIdsRef and send
  // live_subscribe/live_unsubscribe deltas on the open socket without
  // re-opening the WS on every filter change (P4).
  useEffect(() => {
    const next = (selectedBotIds || []).slice();
    const prev = subscribedBotIdsRef.current || [];
    const added = next.filter((b) => !prev.includes(b));
    const removed = prev.filter((b) => !next.includes(b));
    if (added.length === 0 && removed.length === 0) return;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        if (added.length > 0) {
          ws.send(JSON.stringify({ type: 'live_subscribe', bot_ids: added }));
        }
        if (removed.length > 0) {
          ws.send(JSON.stringify({ type: 'live_unsubscribe', bot_ids: removed }));
        }
        subscribedBotIdsRef.current = next;
      } catch (err) {
        console.error('LiveInbox WS sub-diff send error:', err);
      }
    } else {
      // Queue the delta — flushed in onopen
      for (const b of added) {
        pendingSubsRef.current.add.add(b);
        pendingSubsRef.current.remove.delete(b);
      }
      for (const b of removed) {
        pendingSubsRef.current.remove.add(b);
        pendingSubsRef.current.add.delete(b);
      }
    }
  }, [selectedBotIds]);

  // P8.2 & P8.3: Cleanup all timer refs on unmount to prevent timer leaks
  useEffect(() => {
    return () => {
      clearTimeout(agentTypingTimerRef.current);
      Object.values(visitorTypingTimerRef.current).forEach(clearTimeout);
      // L2: also reset the dict so stale sessionId keys don't survive remount
      visitorTypingTimerRef.current = {};
      clearTimeout(msgPollTimerRef.current);
      clearTimeout(sessionPollTimerRef.current);
      clearTimeout(connectingTimerRef.current);
    };
  }, []);

  // Task 4 — Session poll: setTimeout chain
  useEffect(() => {
    if (selectedBotIds.length === 0) return;

    fetchSessions(); // initial load
    scheduleSessionPoll();

    return () => clearTimeout(sessionPollTimerRef.current);
  }, [selectedBotIds, fetchSessions, scheduleSessionPoll]);

  // Task 4 — Message poll: setTimeout chain when joined or viewing
  useEffect(() => {
    const hasActive = joinedSessionId || viewingSessionId || connectingJoin;
    if (!hasActive) {
      clearTimeout(msgPollTimerRef.current);
      return;
    }
    // Fire immediately + schedule chain
    fetchJoinedMessagesRef.current?.();
    scheduleMsgPoll();

    return () => clearTimeout(msgPollTimerRef.current);
  }, [joinedSessionId, viewingSessionId, connectingJoin, scheduleMsgPoll]);

  // Scroll chat to bottom when messages update or visitor starts typing
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages, visitorTyping]);

  // When the agent toggles the chat-bot selector, drop out of any active
  // conversation view whose bot is no longer in the selected set so the
  // inbox below only shows sessions for the currently-selected bots.
  // The session itself is NOT left on the backend — the agent stays joined
  // and the app-level notification provider keeps surfacing new visitor
  // messages — we just close the local view and clear in-flight UI state.
  useEffect(() => {
    const activeSid = joinedSessionId || viewingSessionId || (connectingJoin && connectingJoin.sessionId);
    if (!activeSid) return;
    const conv = activeConversationsRef.current.find(c => c.sessionId === activeSid);
    // If we don't have the conversation in the current list, or its bot
    // isn't selected anymore, exit the view.
    if (conv && selectedBotIds.includes(conv.botId)) return;
    sendAgentTypingStop();
    clearTimeout(connectingTimerRef.current);
    connectingTimerRef.current = null;
    resetAssistStateForSession(activeSid);
    setConnectingJoin(null);
    setJoinedSessionId(null);
    setViewingSessionId(null);
    setChatMessages([]);
    setReplyText('');
    lastSeenMsgIdRef.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBotIds]);

  // Toggle online/offline for a bot — persists to backend
  const handleToggleOnline = async (botId) => {
    const newStatus = onlineStatus[botId] ? 'offline' : 'online';
    setTogglingStatus(prev => ({ ...prev, [botId]: true }));
    try {
      const res = await authFetch(`${API}live/agent_status/${botId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      if (res && res.ok) {
        setOnlineStatus(prev => ({ ...prev, [botId]: newStatus === 'online' }));
        // P7 — start/stop heartbeat for this bot
        setAgentOnline(botId, newStatus === 'online');
        // Refresh the team roster so the user's own dot updates without
        // waiting for the 30s poll.
        fetchTeamAgents([botId]);
        // Let an open test-chat playground re-check availability now so the
        // "Talk to a human" button appears/disappears immediately.
        window.dispatchEvent(new CustomEvent('ub:agent-availability-changed', {
          detail: { botId, online: newStatus === 'online' },
        }));
      }
    } catch (err) {
      console.error('Error toggling agent status:', err);
    }
    setTogglingStatus(prev => ({ ...prev, [botId]: false }));
  };

  // Task 1 — Agent typing: send typing_start/stop over WS
  const handleReplyTextChange = (e) => {
    const value = e.target.value;
    setReplyText(value);

    if (!joinedSessionId) return;

    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    if (value.trim()) {
      // Input has text — send typing_start if not already flagged
      if (!agentIsTypingRef.current) {
        ws.send(JSON.stringify({ type: 'typing_start', session_id: joinedSessionId }));
        agentIsTypingRef.current = true;
      }
      // No debounce timeout — typing stays active as long as input has text
      clearTimeout(agentTypingTimerRef.current);
    } else {
      // Input is empty — send typing_stop
      clearTimeout(agentTypingTimerRef.current);
      if (agentIsTypingRef.current) {
        ws.send(JSON.stringify({ type: 'typing_stop', session_id: joinedSessionId }));
        agentIsTypingRef.current = false;
      }
    }
  };

  // Task 1 — send typing_stop immediately when message is submitted
  const sendAgentTypingStop = () => {
    clearTimeout(agentTypingTimerRef.current);
    if (agentIsTypingRef.current) {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN && joinedSessionId) {
        ws.send(JSON.stringify({ type: 'typing_stop', session_id: joinedSessionId }));
      }
      agentIsTypingRef.current = false;
    }
  };

  // View a conversation (read-only, no status change)
  const handleViewConversation = async (sessionId) => {
    // P5.3: Reset agent typing state on session switch
    agentIsTypingRef.current = false;
    clearTimeout(agentTypingTimerRef.current);
    setJoiningSession(true);
    try {
      const res = await authFetch(`${API}live/messages/${sessionId}?after_id=0`);
      if (!res || !res.ok) {
        console.error('Failed to view session');
        setJoiningSession(false);
        return;
      }
      const data = await res.json();
      const msgs = data.messages || [];

      const maxId = msgs.reduce((max, m) => Math.max(max, m.id || 0), 0);
      lastSeenMsgIdRef.current = maxId;

      setViewingSessionId(sessionId);
      setJoinedSessionId(null);
      setChatMessages(msgs);

      // Mark as read (fire-and-forget)
      authFetch(`${API}live/read/${sessionId}`, { method: 'POST' }).catch(() => {});
      setActiveConversations(prev =>
        prev.map(c => c.sessionId === sessionId ? { ...c, unreadCount: 0 } : c)
      );
    } catch (err) {
      console.error('Error viewing conversation:', err);
    }
    setJoiningSession(false);
  };

  // Reset per-session agent-assist state (translations, outgoing preview).
  const resetAssistStateForSession = (sid) => {
    if (!sid) return;
    setMessageTranslations(prev => {
      if (!prev[sid]) return prev;
      const next = { ...prev };
      delete next[sid];
      return next;
    });
    setMessageLang(prev => {
      if (!prev[sid]) return prev;
      const next = { ...prev };
      delete next[sid];
      return next;
    });
    setSessionTargetLang(prev => {
      if (!(sid in prev)) return prev;
      const next = { ...prev };
      delete next[sid];
      return next;
    });
    setInputLangPickerOpen(false);
    setInputTranslating(false);
  };

  // Leave view mode
  const handleLeaveViewing = () => {
    resetAssistStateForSession(viewingSessionId);
    setViewingSessionId(null);
    setChatMessages([]);
    lastSeenMsgIdRef.current = 0;
  };

  // Open the join modal before actually joining
  const requestJoin = (sessionId) => {
    setJoinModalSessionId(sessionId);
    setJoinModalOpen(true);
  };

  // Join a conversation (from list or from view mode)
  // Flow:
  //   1. POST /live/join                               → DB status = agent_joined
  //   2. Backend pushes `live_agent_joined` to widget
  //   3. Widget renders "Agent has joined" + sends `join_ack` back
  //   4. Backend relays `join_confirmed` to this portal
  //   5. Portal promotes `connectingJoin` → `joinedSessionId` (chat unlocks)
  // Timeout (JOIN_CONFIRM_TIMEOUT_MS): if widget doesn't confirm we mark the
  // connect state as 'timeout' so the agent can still write but sees a banner
  // warning that the visitor may be offline.
  const handleJoinConversation = async (sessionId, agentName) => {
    // P5.3: Reset agent typing state on session switch
    agentIsTypingRef.current = false;
    clearTimeout(agentTypingTimerRef.current);
    // P2.3: Reset watermark so any in-flight poll won't corrupt the new session's data
    lastSeenMsgIdRef.current = 0;
    setJoiningSession(true);
    // Clear any previous connecting timer
    if (connectingTimerRef.current) {
      clearTimeout(connectingTimerRef.current);
      connectingTimerRef.current = null;
    }
    try {
      const body = agentName ? JSON.stringify({ agent_name: agentName }) : undefined;
      const res = await authFetch(`${API}live/join/${sessionId}`, { method: 'POST', body });
      if (!res || !res.ok) {
        console.error('Failed to join session');
        setJoiningSession(false);
        return;
      }
      const data = await res.json();
      const msgs = data.messages || [];

      // Set high-water mark to max DB id from the initial load
      const maxId = msgs.reduce((max, m) => Math.max(max, m.id || 0), 0);
      lastSeenMsgIdRef.current = maxId;

      setViewingSessionId(null);
      // IMPORTANT: do NOT set joinedSessionId yet — we wait for the widget's
      // join_ack (relayed as join_confirmed) before unlocking the chat input.
      setConnectingJoin({ sessionId, state: 'waiting' });
      setChatMessages(msgs);
      // P4.2: Clear visitor typing state on view→join transition
      setVisitorTyping(prev => {
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
      // Update conversation status locally
      if (data.session) {
        setActiveConversations(prev =>
          prev.map(c => c.sessionId === sessionId
            ? { ...c, status: data.session.status, agentJoined: true }
            : c
          )
        );
      }

      // Arm the timeout. If the widget never confirms, keep the connecting
      // banner visible but flip to 'timeout' so the UI can degrade gracefully.
      connectingTimerRef.current = setTimeout(() => {
        setConnectingJoin(prev => {
          if (!prev || prev.sessionId !== sessionId) return prev;
          // Promote to joined even without confirmation so the agent is not
          // stuck. UI still shows a warning banner via state === 'timeout'.
          setJoinedSessionId(sessionId);
          return { sessionId, state: 'timeout' };
        });
      }, JOIN_CONFIRM_TIMEOUT_MS);

      // Task 3 — Mark conversation as read (fire-and-forget)
      authFetch(`${API}live/read/${sessionId}`, { method: 'POST' }).catch(() => {});
      // Zero out unread count locally
      setActiveConversations(prev =>
        prev.map(c => c.sessionId === sessionId
          ? { ...c, unreadCount: 0 }
          : c
        )
      );
    } catch (err) {
      console.error('Error joining conversation:', err);
    }
    setJoiningSession(false);
  };

  // Re-open a conversation the agent already joined (no API call, just restore UI state)
  const handleOpenConversation = async (sessionId) => {
    agentIsTypingRef.current = false;
    clearTimeout(agentTypingTimerRef.current);
    setJoiningSession(true);
    try {
      const res = await authFetch(`${API}live/messages/${sessionId}?after_id=0`);
      if (!res || !res.ok) {
        console.error('Failed to open session');
        setJoiningSession(false);
        return;
      }
      const data = await res.json();
      const msgs = data.messages || [];
      const maxId = msgs.reduce((max, m) => Math.max(max, m.id || 0), 0);
      lastSeenMsgIdRef.current = maxId;

      setViewingSessionId(null);
      setJoinedSessionId(sessionId);
      setChatMessages(msgs);

      authFetch(`${API}live/read/${sessionId}`, { method: 'POST' }).catch(() => {});
      setActiveConversations(prev =>
        prev.map(c => c.sessionId === sessionId ? { ...c, unreadCount: 0 } : c)
      );
    } catch (err) {
      console.error('Error opening conversation:', err);
    }
    setJoiningSession(false);
  };

  // -- Deep-link opener ------------------------------------------------------
  // Opens the conversation a notification linked to (URL params via the
  // LiveChat page, or the in-app toast's `live-notify-open` event). Waits
  // until (a) the session list has loaded and (b) that list covers the
  // deep-linked bot — the global selection may still be switching to it —
  // then mirrors a click on the list item: agent already joined -> open,
  // otherwise -> view (the Join button is right there). A session that is
  // no longer active gets an explanatory notice instead of a silent no-op.
  const openLinkedConversation = (sessionId) => {
    const conv = activeConversations.find((c) => c.sessionId === sessionId);
    if (!conv) {
      setDeepLinkNotice({ kind: 'ended', sessionId });
      return false;
    }
    if (conv.agentJoined) handleOpenConversation(conv.sessionId);
    else handleViewConversation(conv.sessionId);
    return true;
  };

  const DEEP_LINK_WAIT_MS = 8000;
  useEffect(() => {
    if (!deepLink || !deepLink.sessionId) return undefined;
    if (handledDeepLinkRef.current === deepLink) return undefined;
    // Arm the give-up timer once per deep-link object.
    if (deepLinkWaitTimerRef.current === null) {
      deepLinkWaitTimerRef.current = setTimeout(() => {
        deepLinkWaitTimerRef.current = null;
        setDeepLinkWaitExpiredFor(deepLink);
      }, DEEP_LINK_WAIT_MS);
    }
    if (loadingSessions) return undefined;
    const fetched = lastFetchedBotIdsRef.current;
    if (!fetched) return undefined;
    const waitExpired = deepLinkWaitExpiredFor === deepLink;
    if (deepLink.botId && !waitExpired) {
      const botKnown = bots.some((b) => String(b.id) === String(deepLink.botId));
      // Known bot but the list doesn't cover it yet -> wait for the
      // selection to catch up (fetchSessions re-runs on selectedBotIds).
      if (botKnown && !fetched.includes(String(deepLink.botId))) return undefined;
    }
    clearTimeout(deepLinkWaitTimerRef.current);
    deepLinkWaitTimerRef.current = null;
    handledDeepLinkRef.current = deepLink;
    openLinkedConversation(deepLink.sessionId);
    if (typeof onDeepLinkConsumed === 'function') onDeepLinkConsumed();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink, loadingSessions, activeConversations, bots, onDeepLinkConsumed, deepLinkWaitExpiredFor]);

  // Timer hygiene on unmount.
  useEffect(() => () => clearTimeout(deepLinkWaitTimerRef.current), []);

  // In-page toast (already on /live): LiveNotificationsProvider dispatches
  // `live-notify-open` with the session; the bot switch itself is handled by
  // SelectedBotContext. Queue it and open once the list covers that bot.
  useEffect(() => {
    const onNotifyOpen = (e) => {
      const sid = e?.detail?.sessionId;
      const bid = e?.detail?.botId;
      if (!sid) return;
      setToastOpenRequest({ botId: bid ? String(bid) : null, sessionId: String(sid) });
    };
    window.addEventListener('live-notify-open', onNotifyOpen);
    return () => window.removeEventListener('live-notify-open', onNotifyOpen);
  }, []);

  useEffect(() => {
    if (!toastOpenRequest) return;
    if (loadingSessions) return;
    const fetched = lastFetchedBotIdsRef.current;
    if (!fetched) return;
    if (toastOpenRequest.botId && !fetched.includes(toastOpenRequest.botId)) return;
    const req = toastOpenRequest;
    setToastOpenRequest(null);
    openLinkedConversation(req.sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastOpenRequest, loadingSessions, activeConversations]);

  // Go back to conversation list without leaving the conversation
  const handleBackFromConversation = () => {
    sendAgentTypingStop();
    clearTimeout(connectingTimerRef.current);
    connectingTimerRef.current = null;
    resetAssistStateForSession(joinedSessionId || (connectingJoin && connectingJoin.sessionId));
    setConnectingJoin(null);
    setJoinedSessionId(null);
    setChatMessages([]);
    setReplyText('');
    lastSeenMsgIdRef.current = 0;
  };

  // Leave conversation (actually disconnect agent)
  const handleLeaveConversation = async (sessionId) => {
    const sid = sessionId || joinedSessionId || (connectingJoin && connectingJoin.sessionId);
    setLeavingSessionId(sid);
    sendAgentTypingStop();
    clearTimeout(connectingTimerRef.current);
    connectingTimerRef.current = null;
    if (sid === joinedSessionId || (connectingJoin && sid === connectingJoin.sessionId)) {
      resetAssistStateForSession(sid);
      setConnectingJoin(null);
      setJoinedSessionId(null);
      setChatMessages([]);
      setReplyText('');
      lastSeenMsgIdRef.current = 0;
    }
    try {
      await authFetch(`${API}live/leave/${sid}`, { method: 'POST' });
      // Optimistically update the conversation status so the Leave button disappears immediately
      setActiveConversations(prev =>
        prev.map(c => c.sessionId === sid
          ? { ...c, status: 'active', agentJoined: false, requestedHelp: false }
          : c
        )
      );
      // Also refresh from DB for accurate state
      fetchSessions();
    } catch (err) {
      console.error('Error leaving conversation:', err);
    }
    setLeavingSessionId(null);
  };

  // Send message in joined conversation
  const handleSendMessage = async (overrideContent) => {
    const message = (typeof overrideContent === 'string' ? overrideContent : replyText).trim();
    if (!message || !joinedSessionId) return;

    // Task 1 — stop typing indicator immediately
    sendAgentTypingStop();

    setSendingMessage(true);
    // Add optimistically
    // P2.1: Add tempId to make deduplication reliable
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const newMessage = {
      id: Date.now(),
      tempId,
      type: 'agent',
      content: message,
      timestamp: new Date().toISOString(),
    };
    setChatMessages(prev => [...prev, newMessage]);
    setReplyText('');

    try {
      const res = await authFetch(`${API}live/send/${joinedSessionId}`, {
        method: 'POST',
        body: JSON.stringify({ content: message }),
      });
      if (!res || !res.ok) {
        console.error('Failed to send message');
        // Remove optimistic message on failure
        setChatMessages(prev => prev.filter(m => m.id !== newMessage.id));
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setChatMessages(prev => prev.filter(m => m.id !== newMessage.id));
    }
    setSendingMessage(false);
  };

  // Task 2 — Derive msg-status class and label (shown on agent's own bubbles)
  const getMsgStatus = (msg) => {
    if (msg.type !== 'agent') return null;
    if (msg.read_at) return { cls: 'read', label: '✓✓ Read' };
    if (msg.delivered_at) return { cls: 'delivered', label: '✓✓ Delivered' };
    return { cls: 'sent', label: '✓ Sent' };
  };

  // ────────────────────────────────────────────────────────────────────
  // Agent assist helpers: translation + AI-suggested replies
  // ────────────────────────────────────────────────────────────────────
  const SUPPORTED_TRANSLATION_LANGS = [
    { code: 'en', label: 'English' },
    { code: 'de', label: 'German' },
    { code: 'es', label: 'Spanish' },
    { code: 'fr', label: 'French' },
    { code: 'it', label: 'Italian' },
    { code: 'pt', label: 'Portuguese' },
    { code: 'nl', label: 'Dutch' },
    { code: 'pl', label: 'Polish' },
    { code: 'tr', label: 'Turkish' },
    { code: 'ru', label: 'Russian' },
    { code: 'ar', label: 'Arabic' },
    { code: 'zh', label: 'Chinese' },
    { code: 'ja', label: 'Japanese' },
    { code: 'ko', label: 'Korean' },
  ];

  // Per-message translate selector. `cc` is the ISO 3166-1 alpha-2 country
  // code used by flagcdn.com (Windows can't render flag emojis natively, so
  // we serve real PNG flags instead).
  const LANGS_WITH_FLAG = [
    { code: 'en', cc: 'gb', label: 'English' },
    { code: 'de', cc: 'de', label: 'German' },
    { code: 'es', cc: 'es', label: 'Spanish' },
    { code: 'fr', cc: 'fr', label: 'French' },
    { code: 'it', cc: 'it', label: 'Italian' },
    { code: 'pt', cc: 'pt', label: 'Portuguese' },
    { code: 'nl', cc: 'nl', label: 'Dutch' },
    { code: 'pl', cc: 'pl', label: 'Polish' },
    { code: 'tr', cc: 'tr', label: 'Turkish' },
    { code: 'ru', cc: 'ru', label: 'Russian' },
    { code: 'ar', cc: 'sa', label: 'Arabic' },
    { code: 'zh', cc: 'cn', label: 'Chinese' },
    { code: 'ja', cc: 'jp', label: 'Japanese' },
    { code: 'ko', cc: 'kr', label: 'Korean' },
  ];

  const flagUrl = (cc) => `https://flagcdn.com/w20/${cc}.png`;
  const flagUrl2x = (cc) => `https://flagcdn.com/w40/${cc}.png 2x`;

  const getLangLabel = (code) => {
    if (!code) return '';
    const found =
      LANGS_WITH_FLAG.find(l => l.code === code) ||
      SUPPORTED_TRANSLATION_LANGS.find(l => l.code === code || l.label.toLowerCase() === code.toLowerCase());
    return found ? found.label : code;
  };

  // Get / set the per-message target language. Defaults to the session-level
  // language if set, else English.
  const getMessageLangCode = (sessionId, msgId) => {
    const perMsg = messageLang[sessionId]?.[msgId];
    if (perMsg) return perMsg;
    const sessionDefault = sessionTargetLang[sessionId];
    if (sessionDefault) {
      const match = LANGS_WITH_FLAG.find(l => l.label.toLowerCase() === String(sessionDefault).toLowerCase());
      if (match) return match.code;
    }
    return 'en';
  };

  const setMessageLangCode = (sessionId, msgId, code) => {
    setMessageLang(prev => ({
      ...prev,
      [sessionId]: { ...(prev[sessionId] || {}), [msgId]: code },
    }));
    // Remember this choice as the default for future messages in this session.
    const match = LANGS_WITH_FLAG.find(l => l.code === code);
    if (match) {
      setSessionTargetLang(prev => ({ ...prev, [sessionId]: match.label }));
    }
    // Clear any stale translation for this message so the user re-clicks "Translate"
    setMessageTranslations(prev => {
      if (!prev[sessionId]?.[msgId]) return prev;
      const sessionMap = { ...(prev[sessionId] || {}) };
      delete sessionMap[msgId];
      return { ...prev, [sessionId]: sessionMap };
    });
  };

  // ── Plan gating helpers for agent-assist features. Mirrors the pattern in
  // knowledgeOverview.js / rescrapingSettingsModal.js: check the bot's
  // subscription_plan.name against a minimum tier, and if the tier isn't
  // met open an upgrade modal that sends the agent into the standard
  // Stripe/Wix upgrade flow via useUpgrade().
  const getBotForSession = useCallback((sessionId) => {
    const conv = activeConversationsRef.current.find(c => c.sessionId === sessionId);
    if (!conv) return null;
    return bots.find(b => b.id === conv.botId) || null;
  }, [bots]);

  const hasBoostPlan = useCallback((bot) => {
    const name = bot?.subscription_plan?.name;
    return name === 'Boost' || name === 'Ultimo';
  }, []);

  const hasSmartPlan = useCallback((bot) => {
    const name = bot?.subscription_plan?.name;
    return name === 'Smart' || name === 'Boost' || name === 'Ultimo';
  }, []);

  // Toggle / request translation for a single visitor message bubble.
  // `targetLangCode` is the ISO code from the per-message selector.
  const toggleMessageTranslation = useCallback(async (sessionId, msg, targetLangCode) => {
    const code = targetLangCode || 'en';
    const targetLabel = (LANGS_WITH_FLAG.find(l => l.code === code) || {}).label || code;
    const existing = messageTranslations[sessionId]?.[msg.id];
    // Toggle off if already shown
    if (existing && existing.state === 'shown') {
      setMessageTranslations(prev => ({
        ...prev,
        [sessionId]: { ...(prev[sessionId] || {}), [msg.id]: { ...existing, state: 'hidden' } },
      }));
      return;
    }
    // Re-show cached translation in same lang without a network round-trip
    if (existing && existing.state === 'hidden' && existing.text && existing.langCode === code) {
      setMessageTranslations(prev => ({
        ...prev,
        [sessionId]: { ...(prev[sessionId] || {}), [msg.id]: { ...existing, state: 'shown' } },
      }));
      return;
    }
    // Plan gate: Translate requires Smart+.
    const bot = getBotForSession(sessionId);
    if (!hasSmartPlan(bot)) {
      setAssistUpgradeModal({
        bot,
        title: t('live_inbox_translate_upgrade_title') || 'Premium Feature',
        description: t('live_inbox_translate_upgrade_desc') || 'Translate messages you write (and replies you receive) into your visitor\u2019s language. Upgrade to Smart, Boost, or Ultimo to unlock translation.',
        requiredPlan: 'Smart+',
      });
      return;
    }
    setMessageTranslations(prev => ({
      ...prev,
      [sessionId]: {
        ...(prev[sessionId] || {}),
        [msg.id]: { state: 'loading', text: '', lang: targetLabel, langCode: code },
      },
    }));
    try {
      const res = await authFetch(`${API}live/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: msg.content,
          target_language: targetLabel,
          session_id: sessionId,
        }),
      });
      if (!res.ok) throw new Error('translate failed');
      const data = await res.json();
      setMessageTranslations(prev => ({
        ...prev,
        [sessionId]: {
          ...(prev[sessionId] || {}),
          [msg.id]: { state: 'shown', text: data.translated_text || '', lang: targetLabel, langCode: code },
        },
      }));
    } catch (err) {
      setMessageTranslations(prev => ({
        ...prev,
        [sessionId]: {
          ...(prev[sessionId] || {}),
          [msg.id]: { state: 'error', text: '', lang: targetLabel, langCode: code },
        },
      }));
    }
  }, [messageTranslations, getBotForSession, hasSmartPlan, t]);

  // Fetch remaining credits for the bot tied to the active session.
  const fetchCredits = useCallback(async (botId) => {
    if (!botId) return;
    try {
      const res = await authFetch(`${API}live/credits/${botId}`);
      if (!res.ok) return;
      const data = await res.json();
      setCreditsLeft(prev => ({ ...prev, [botId]: data.messages_left ?? 0 }));
    } catch (_) { /* non-fatal */ }
  }, []);

  // Request an AI-suggested reply; fills replyText on success.
  const requestSuggestion = useCallback(async () => {
    if (!joinedSessionId || suggesting) return;
    const conv = activeConversationsRef.current.find(c => c.sessionId === joinedSessionId);
    if (!conv) return;
    // Plan gate: Suggest requires Boost+.
    const bot = bots.find(b => b.id === conv.botId) || null;
    if (!hasBoostPlan(bot)) {
      setAssistUpgradeModal({
        bot,
        title: t('live_inbox_suggest_upgrade_title') || 'Premium Feature',
        description: t('live_inbox_suggest_upgrade_desc') || 'AI-suggested replies use your bot\u2019s knowledge base to draft a response for you. Upgrade to Boost or Ultimo to enable this feature.',
        requiredPlan: 'Boost+',
      });
      return;
    }
    const controller = new AbortController();
    suggestAbortRef.current = controller;
    setSuggesting(true);
    try {
      const res = await authFetch(`${API}live/suggest_reply/${joinedSessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
        signal: controller.signal,
      });
      if (!res.ok) {
        if (res.status === 402) {
          alert(t('live_inbox_credits_exhausted') || 'Message limit reached for this bot. Upgrade the plan to generate more suggestions.');
        }
        return;
      }
      const data = await res.json();
      if (typeof data.suggestion === 'string') {
        setReplyText(data.suggestion);
      }
      if (typeof data.messages_left === 'number') {
        setCreditsLeft(prev => ({ ...prev, [conv.botId]: data.messages_left }));
      }
    } catch (err) {
      // Aborted requests just exit silently; other failures keep the UI as-is.
      if (err && err.name === 'AbortError') return;
    } finally {
      if (suggestAbortRef.current === controller) suggestAbortRef.current = null;
      setSuggesting(false);
    }
  }, [joinedSessionId, suggesting, t, bots, hasBoostPlan]);

  const cancelSuggestion = useCallback(() => {
    if (suggestAbortRef.current) {
      suggestAbortRef.current.abort();
      suggestAbortRef.current = null;
    }
    setSuggesting(false);
  }, []);

  // Translate the currently-drafted reply text in-place. The textarea content
  // is replaced with the translated text; while the request is in flight a
  // spinner is shown over the input. Picked language code (e.g. 'de') maps to
  // a human label ('German') for the backend.
  const translateInputInPlace = useCallback(async (targetLangCode) => {
    if (!joinedSessionId || inputTranslating) return;
    const original = (replyText || '').trim();
    if (!original) return;
    // Plan gate: Translate requires Smart+.
    const bot = getBotForSession(joinedSessionId);
    if (!hasSmartPlan(bot)) {
      setAssistUpgradeModal({
        bot,
        title: t('live_inbox_translate_upgrade_title') || 'Premium Feature',
        description: t('live_inbox_translate_upgrade_desc') || 'Translate messages you write (and replies you receive) into your visitor\u2019s language. Upgrade to Smart, Boost, or Ultimo to unlock translation.',
        requiredPlan: 'Smart+',
      });
      return;
    }
    const match = LANGS_WITH_FLAG.find(l => l.code === targetLangCode);
    const targetLabel = match ? match.label : targetLangCode;
    const controller = new AbortController();
    translateAbortRef.current = controller;
    setInputTranslating(true);
    try {
      const res = await authFetch(`${API}live/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: original,
          target_language: targetLabel,
          session_id: joinedSessionId,
        }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error('translate failed');
      const data = await res.json();
      if (data.translated_text) {
        setReplyText(data.translated_text);
        // Remember the language as the session default so the per-message
        // pickers and future input translations share the same preference.
        if (match) {
          setSessionTargetLang(prev => ({ ...prev, [joinedSessionId]: match.label }));
        }
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      alert(t('live_inbox_translate_failed') || 'Translation failed. Please try again.');
    } finally {
      if (translateAbortRef.current === controller) translateAbortRef.current = null;
      setInputTranslating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinedSessionId, inputTranslating, replyText, t, getBotForSession, hasSmartPlan]);

  const cancelTranslation = useCallback(() => {
    if (translateAbortRef.current) {
      translateAbortRef.current.abort();
      translateAbortRef.current = null;
    }
    setInputTranslating(false);
  }, []);

  // When the agent joins a conversation, fetch credits for that bot once.
  useEffect(() => {
    if (!joinedSessionId) return;
    const conv = activeConversationsRef.current.find(c => c.sessionId === joinedSessionId);
    if (conv?.botId) fetchCredits(conv.botId);
  }, [joinedSessionId, fetchCredits]);

  // Close the per-message language picker on outside click / Escape.
  // The menu is rendered into a portal (see MessageLangMenu) so we must also
  // treat clicks inside `.message-lang-menu` as "inside" the picker.
  useEffect(() => {
    if (!langPickerOpen) return;
    const onDocClick = (e) => {
      if (!e.target.closest) { setLangPickerOpen(null); setLangPickerAnchor(null); return; }
      if (e.target.closest('.message-lang-picker')) return;
      if (e.target.closest('.message-lang-menu')) return;
      setLangPickerOpen(null);
      setLangPickerAnchor(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setLangPickerOpen(null); setLangPickerAnchor(null); }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [langPickerOpen]);

  // Close the input-area language picker on outside click / Escape.
  useEffect(() => {
    if (!inputLangPickerOpen) return;
    const onDocClick = (e) => {
      if (!e.target.closest || !e.target.closest('.live-inbox-input-translate')) {
        setInputLangPickerOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setInputLangPickerOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [inputLangPickerOpen]);

  // All currently selected bots — every bot is implicitly "live-chat capable"
  // now that the per-bot enable toggle has been removed; visitor handover is
  // gated only by the agent's Online/Offline pill (handleToggleOnline).
  const enabledBots = bots.filter(bot => selectedBotIds.includes(bot.id));

  const hasEnabledBots = enabledBots.length > 0;

  // Show a single full-component spinner ONLY for the very first load. Once
  // we've fired onReady, never collapse back to the spinner — bot switches
  // and re-fetches keep the inbox UI visible (small per-section spinners
  // handle in-place loading) so we never flicker reveal → spinner → reveal.
  const isInitializing =
    loadingSettings ||
    loadingSessions ||
    (bots.length > 0 && selectedBotIds.length === 0);

  // Notify the parent exactly once when the component has finished its
  // initial loading. Lets the page render the bot selector + inbox in a
  // single coordinated reveal instead of flashing the selector / status bar
  // / empty state into view one by one.
  const readyFiredRef = useRef(false);
  const [hasFiredReady, setHasFiredReady] = useState(false);
  useEffect(() => {
    if (!isInitializing && !readyFiredRef.current) {
      readyFiredRef.current = true;
      setHasFiredReady(true);
      if (typeof onReady === 'function') onReady();
    }
  }, [isInitializing, onReady]);

  if (isInitializing && !hasFiredReady) {
    return (
      <div className="live-inbox-loading">
        <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#5616ea', fontSize: '20px' }} />
      </div>
    );
  }

  // No bots have live chat enabled
  // (skip gate — conversations always appear; status bar only shown for enabled bots)

  // Viewing or joined conversation view
  const activeSessionId = joinedSessionId || viewingSessionId || (connectingJoin && connectingJoin.sessionId);
  const isViewOnly = !joinedSessionId && !connectingJoin && !!viewingSessionId;
  const isConnecting = !!connectingJoin && !joinedSessionId; // strict: only true during wait

  if (activeSessionId) {
    const conversation = activeConversations.find(c => c.sessionId === activeSessionId);
    // Task 5 — presence for chat header
    const presenceStatus = getVisitorPresenceStatus(conversation?.lastActivity, conversation?.visibility);
    // Richer labels during the connection gap so the agent has explicit
    // feedback that the visitor is briefly unreachable (refresh / short
    // outage) or has likely disconnected.
    const presenceLabel =
      presenceStatus === 'online'
        ? (t('live_inbox_presence_online') || 'Online')
        : presenceStatus === 'background'
          ? (t('live_inbox_presence_background') || 'In the background')
          : presenceStatus === 'away'
            ? (t('live_inbox_presence_reconnecting') || 'Reconnecting…')
            : (t('live_inbox_presence_connection_lost') || 'Connection lost');
    const presenceColor = {
      online: '#22c55e', background: '#94a3b8', away: '#f59e0b',
    }[presenceStatus] || '#ef4444';

    return (
      <>
      <div className={`live-inbox-chat${isConnecting ? ' is-connecting' : ''}`}>
        {isConnecting && (
          <div className="live-inbox-connecting-overlay" role="status" aria-live="polite">
            <FontAwesomeIcon icon={faSpinner} spin className="live-inbox-connecting-overlay-spinner" />
            <div className="live-inbox-connecting-overlay-title">
              {t('live_inbox_connecting') || 'Connecting to visitor…'}
            </div>
            <div className="live-inbox-connecting-overlay-subtitle">
              {t('live_inbox_connecting_subtitle') || 'Waiting for the visitor to confirm the connection.'}
            </div>
            <button
              className="live-inbox-leave-btn live-inbox-connecting-overlay-cancel"
              onClick={() => {
                const conv = activeConversationsRef.current.find(c => c.sessionId === activeSessionId);
                setLeaveConfirm({ sessionId: activeSessionId, visitorName: conv?.visitorName || '' });
              }}
              disabled={leavingSessionId === activeSessionId}
            >
              {leavingSessionId === activeSessionId ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faArrowRightFromBracket} />
              )}
              <span>{t('live_inbox_cancel') || 'Cancel'}</span>
            </button>
          </div>
        )}
        {/* Chat header */}
        <div className="live-inbox-chat-header">
          <button className="live-inbox-back-btn" onClick={isViewOnly ? handleLeaveViewing : handleBackFromConversation}>
            <FontAwesomeIcon icon={faArrowLeft} /> {t('live_inbox_back') || 'Back'}
          </button>
          <div className="live-inbox-chat-header-info">
            <span className="live-inbox-chat-visitor-name">
              {conversation?.visitorName || t('live_inbox_visitor') || 'Visitor'}
            </span>
            <span className="live-inbox-chat-bot-name">
              {conversation?.botName || ''}
              {isViewOnly && <span className="live-inbox-view-badge">{t('live_inbox_viewing') || 'Viewing'}</span>}
            </span>
          </div>
          {/* Task 5 — dynamic presence dot */}
          <div
            className={`live-inbox-chat-status presence-${presenceStatus}`}
            title={presenceStatus === 'background' ? t('live_inbox_presence_background_hint') : undefined}
          >
            <FontAwesomeIcon
              icon={faCircle}
              className={`status-dot ${presenceStatus}`}
              style={{ color: presenceColor }}
            />
            {presenceLabel}
          </div>
          {/* Leave button — only when actually joined (not view-only) */}
          {!isViewOnly && (joinedSessionId === activeSessionId || (connectingJoin && connectingJoin.sessionId === activeSessionId)) && (
            <button
              className="live-inbox-leave-btn"
              onClick={() => {
                const conv = activeConversationsRef.current.find(c => c.sessionId === activeSessionId);
                setLeaveConfirm({ sessionId: activeSessionId, visitorName: conv?.visitorName || '' });
              }}
              disabled={leavingSessionId === activeSessionId}
            >
              {leavingSessionId === activeSessionId ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faArrowRightFromBracket} />
              )}
              <span>{t('live_inbox_leave') || 'Leave'}</span>
            </button>
          )}
          {/* Join button in view mode */}
          {isViewOnly && (
            <button
              className="live-inbox-join-btn"
              onClick={() => requestJoin(activeSessionId)}
              disabled={joiningSession}
            >
              {joiningSession ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faArrowRightToBracket} />
              )}
              <span>{conversation?.agentJoined
                ? (t('live_inbox_take_over') || 'Take over')
                : (t('live_inbox_join') || 'Join')
              }</span>
            </button>
          )}
        </div>

        {/* Chat messages */}
        <div className="live-inbox-chat-messages" ref={chatMessagesRef}>
          {/* Handshake banners */}
          {!isConnecting && connectingJoin && connectingJoin.state === 'timeout' && connectingJoin.sessionId === activeSessionId && (
            <div className="live-inbox-connecting-banner warn live-inbox-connecting-banner-sticky" role="status">
              <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#f59e0b', fontSize: '14px' }} />
              <span>
                {t('live_inbox_visitor_offline_warning') || 'The visitor has not confirmed the connection. They may be offline — messages will be delivered when they return.'}
              </span>
            </div>
          )}
          {chatMessages.length === 0 ? (
            <div className="live-inbox-chat-empty">
              <p>{isViewOnly
                ? (t('live_inbox_no_messages_view') || 'No messages in this conversation yet.')
                : (t('live_inbox_no_messages') || 'No messages yet. The visitor is waiting for your response.')
              }</p>
            </div>
          ) : (
            chatMessages.map((msg) => {
              const msgStatus = getMsgStatus(msg);
              const isVisitor = msg.type === 'user_input';
              const tr = isVisitor ? messageTranslations[activeSessionId]?.[msg.id] : null;
              return (
                <div
                  key={msg.id}
                  className={`chat-message ${isVisitor ? 'user-history-message' : 'assistant-message'}`}
                >
                  {isVisitor ? (
                    <div className="user-message-group">
                      <div className="message-bubble-history">
                        <ReactMarkdown
                          components={{ a: LinkRenderer }}
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                        >
                          {msg.content?.replace(/  +/g, ' ')?.replace(/ $/gm, '')}
                        </ReactMarkdown>
                      </div>
                      {/* Per-message translate row: hyperlink + flag/code picker */}
                      {!isViewOnly && msg.id && (() => {
                        const currentCode = getMessageLangCode(activeSessionId, msg.id);
                        const currentLang = LANGS_WITH_FLAG.find(l => l.code === currentCode) || LANGS_WITH_FLAG[0];
                        const isShown = tr && tr.state === 'shown';
                        const isLoading = tr && tr.state === 'loading';
                        const pickerKey = `${activeSessionId}:${msg.id}`;
                        const pickerOpen = langPickerOpen === pickerKey;
                        return (
                          <div className="message-translate-row">
                            <button
                              type="button"
                              className="message-translate-link"
                              onClick={() => toggleMessageTranslation(activeSessionId, msg, currentCode)}
                              disabled={isLoading}
                            >
                              {isLoading
                                ? (t('live_inbox_translating') || 'Translating…')
                                : isShown
                                  ? (t('live_inbox_hide_translation') || 'Hide translation')
                                  : (t('live_inbox_translate') || 'Translate')}
                            </button>
                            <div className="message-lang-picker">
                              <button
                                type="button"
                                className="message-lang-trigger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (pickerOpen) {
                                    setLangPickerOpen(null);
                                    setLangPickerAnchor(null);
                                  } else {
                                    setLangPickerAnchor(e.currentTarget);
                                    setLangPickerOpen(pickerKey);
                                  }
                                }}
                                disabled={isLoading}
                                title={t('live_inbox_choose_language') || 'Choose target language'}
                              >
                                <img
                                  src={flagUrl(currentLang.cc)}
                                  srcSet={flagUrl2x(currentLang.cc)}
                                  alt={currentLang.label}
                                  className="message-lang-flag"
                                  width="20"
                                  height="14"
                                />
                                <span className="message-lang-code">{currentLang.code}</span>
                                <FontAwesomeIcon icon={faAngleDown} className="message-lang-caret" />
                              </button>
                              {pickerOpen && (
                                <MessageLangMenu anchor={langPickerAnchor}>
                                  {LANGS_WITH_FLAG.map(l => (
                                    <button
                                      key={l.code}
                                      type="button"
                                      className={`message-lang-option${l.code === currentCode ? ' is-active' : ''}`}
                                      role="option"
                                      aria-selected={l.code === currentCode}
                                      onClick={() => {
                                        setMessageLangCode(activeSessionId, msg.id, l.code);
                                        setLangPickerOpen(null);
                                        setLangPickerAnchor(null);
                                      }}
                                    >
                                      <img
                                        src={flagUrl(l.cc)}
                                        srcSet={flagUrl2x(l.cc)}
                                        alt={l.label}
                                        className="message-lang-flag"
                                        width="20"
                                        height="14"
                                      />
                                      <span className="message-lang-code">{l.code}</span>
                                      <span className="message-lang-name">{t(`lang_${l.code}`) || l.label}</span>
                                    </button>
                                  ))}
                                </MessageLangMenu>
                              )}
                            </div>
                            {isLoading && (
                              <FontAwesomeIcon icon={faSpinner} spin className="message-translate-spinner" />
                            )}
                          </div>
                        );
                      })()}
                      {/* Translated text shown beneath the bubble */}
                      {tr && tr.state === 'shown' && tr.text && (
                        <div className="message-translation">
                          <div className="message-translation-label">
                            <FontAwesomeIcon icon={faLanguage} />
                            <span>{t('live_inbox_translated_to') || 'Translated to'} {getLangLabel(tr.langCode || tr.lang)}</span>
                          </div>
                          <div className="message-translation-text">
                            <ReactMarkdown
                              components={{ a: LinkRenderer }}
                              remarkPlugins={[remarkGfm]}
                              rehypePlugins={[rehypeRaw]}
                            >
                              {tr.text}
                            </ReactMarkdown>
                          </div>
                        </div>
                      )}
                      {tr && tr.state === 'error' && (
                        <div className="message-translation message-translation-error">
                          {t('live_inbox_translate_failed') || 'Translation failed.'}
                        </div>
                      )}
                      <span className="message-time" style={{ marginTop: 0 }}>
                        {dayjs.utc(msg.timestamp).tz(getUserTimezone()).format('HH:mm')}
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="message-bubble-history">
                        <ReactMarkdown
                          components={{ a: LinkRenderer }}
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                        >
                          {msg.content?.replace(/  +/g, ' ')?.replace(/ $/gm, '')}
                        </ReactMarkdown>
                      </div>
                      <span className="message-time">
                        {dayjs.utc(msg.timestamp).tz(getUserTimezone()).format('HH:mm')}
                        {msgStatus && (
                          <span className={`msg-status ${msgStatus.cls}`}> {msgStatus.label}</span>
                        )}
                      </span>
                    </>
                  )}
                </div>
              );
            })
          )}
          {/* Task 1 — Visitor typing indicator */}
          {visitorTyping[activeSessionId] && (
            <div className="live-inbox-typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
        </div>

        {/* Chat input (only when joined, not in view mode) */}
        {isViewOnly ? (
          <div className="live-inbox-view-footer">
            <button
              className="live-inbox-join-btn live-inbox-join-btn-full"
              onClick={() => requestJoin(activeSessionId)}
              disabled={joiningSession}
            >
              {joiningSession ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faArrowRightToBracket} />
              )}
              <span>{conversation?.agentJoined
                ? (t('live_inbox_take_over_chat') || 'Take over this conversation')
                : (t('live_inbox_join_chat') || 'Join this conversation')
              }</span>
            </button>
          </div>
        ) : isConnecting ? (
          <div className="live-inbox-chat-input">
            <textarea
              rows={1}
              placeholder={t('live_inbox_connecting_placeholder') || 'Waiting for visitor to confirm connection…'}
              value=""
              disabled
              readOnly
            />
            <button className="live-inbox-send-btn" disabled>
              <FontAwesomeIcon icon={faSpinner} spin />
            </button>
          </div>
        ) : (
          <div className="live-inbox-chat-input">
            <div className="live-inbox-chat-input-textwrap">
              <textarea
                ref={(el) => {
                  if (el) {
                    el.style.height = 'auto';
                    const lineHeight = 22; // ~16px * 1.4 line-height
                    const maxLines = 5;
                    const padding = 12; // 6px top + 6px bottom
                    const maxHeight = (lineHeight * maxLines) + padding;
                    const minHeight = lineHeight + padding; // single line + padding
                    const newHeight = el.value ? Math.min(el.scrollHeight, maxHeight) : minHeight;
                    el.style.height = newHeight + 'px';
                    if (el.scrollHeight > maxHeight) {
                      el.classList.add('has-overflow');
                    } else {
                      el.classList.remove('has-overflow');
                    }
                  }
                }}
                rows={1}
                placeholder={t('live_inbox_type_message') || 'Type a message...'}
                value={replyText}
                onChange={handleReplyTextChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={sendingMessage || inputTranslating || suggesting}
              />
              {(inputTranslating || suggesting) && (
                <div className="live-inbox-input-busy-overlay" role="status" aria-live="polite">
                  <FontAwesomeIcon icon={faSpinner} spin className="live-inbox-input-busy-spinner" />
                  <span className="live-inbox-input-busy-label">
                    {inputTranslating
                      ? (t('live_inbox_translating') || 'Translating\u2026')
                      : (t('live_inbox_preparing_suggestion') || 'Preparing a suggested response\u2026')}
                  </span>
                  <button
                    type="button"
                    className="live-inbox-input-busy-cancel"
                    onClick={inputTranslating ? cancelTranslation : cancelSuggestion}
                    title={t('cancel') || 'Cancel'}
                  >
                    {t('cancel') || 'Cancel'}
                  </button>
                </div>
              )}
            </div>

            {/* Suggest reply (AI) — gated on Boost+ plan at click time. */}
            <button
              type="button"
              className="live-inbox-input-action-btn"
              onClick={requestSuggestion}
              disabled={suggesting || sendingMessage || inputTranslating}
              title={t('live_inbox_suggest_reply_title') || 'Generate an AI reply based on your knowledge base'}
              aria-label={t('live_inbox_suggest_reply') || 'Suggest reply'}
            >
              {suggesting ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faWandMagicSparkles} />
              )}
              <span className="live-inbox-input-action-label">
                {t('live_inbox_suggest') || 'Suggest'}
              </span>
            </button>

            {/* Translate the typed reply in-place */}
            <div className="live-inbox-input-translate">
              <button
                type="button"
                className="live-inbox-input-action-btn"
                onClick={() => setInputLangPickerOpen(o => !o)}
                disabled={!replyText.trim() || inputTranslating || sendingMessage}
                title={t('live_inbox_translate_input_title') || 'Translate your message'}
                aria-label={t('live_inbox_translate_input_title') || 'Translate your message'}
                aria-haspopup="listbox"
                aria-expanded={inputLangPickerOpen}
              >
                {inputTranslating ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faLanguage} />
                )}
                <span className="live-inbox-input-action-label">
                  {t('live_inbox_translate_input') || 'Translate input'}
                </span>
              </button>
              {inputLangPickerOpen && (
                <div className="live-inbox-input-translate-menu" role="listbox">
                  {LANGS_WITH_FLAG.map(l => (
                    <button
                      key={l.code}
                      type="button"
                      className="message-lang-option"
                      role="option"
                      onClick={() => {
                        setInputLangPickerOpen(false);
                        translateInputInPlace(l.code);
                      }}
                    >
                      <img
                        src={flagUrl(l.cc)}
                        srcSet={flagUrl2x(l.cc)}
                        alt={l.label}
                        className="message-lang-flag"
                        width="20"
                        height="14"
                      />
                      <span className="message-lang-code">{l.code}</span>
                      <span className="message-lang-name">{t(`lang_${l.code}`) || l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className="live-inbox-send-btn"
              onClick={() => handleSendMessage()}
              disabled={sendingMessage || inputTranslating || !replyText.trim()}
              title={t('live_inbox_send') || 'Send'}
            >
              {sendingMessage ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" className="send-icon">
                  <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0L7 203.6c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 140.3V464c0 17.7 14.3 32 32 32s32-14.3 32-32V140.3l107.6 108.7c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L214.6 41.4z" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
      <JoinConversationModal
        open={joinModalOpen}
        onClose={() => setJoinModalOpen(false)}
        onConfirm={(agentName) => {
          setJoinModalOpen(false);
          if (joinModalSessionId) {
            handleJoinConversation(joinModalSessionId, agentName);
          }
        }}
        defaultAgentName={
          (() => {
            const conv = activeConversations.find(c => c.sessionId === joinModalSessionId);
            return conv ? (liveChatSettings[conv.botId]?.agent_display_name || '') : '';
          })()
        }
      />
      <LeaveConversationModal
        open={!!leaveConfirm}
        visitorName={leaveConfirm?.visitorName || ''}
        onClose={() => setLeaveConfirm(null)}
        onConfirm={() => {
          const sid = leaveConfirm?.sessionId;
          setLeaveConfirm(null);
          if (sid) handleLeaveConversation(sid);
        }}
      />
      <PoweredByUpgradeModal
        open={!!assistUpgradeModal}
        bot={assistUpgradeModal?.bot}
        title={assistUpgradeModal?.title}
        description={assistUpgradeModal?.description}
        requiredPlan={assistUpgradeModal?.requiredPlan}
        onClose={() => setAssistUpgradeModal(null)}
        onUpgrade={() => {
          const b = assistUpgradeModal?.bot;
          setAssistUpgradeModal(null);
          if (b) upgradeClick(b);
        }}
      />
      {AssistSubscriptionModal}
      </>
    );
  }

  // Main live inbox view
  return (
    <div className="live-inbox">
      {/* Availability section per bot — self + teammates stacked */}
      {hasEnabledBots && (
      <div className="live-inbox-status-bar">
        {enabledBots.map(bot => {
          const settings = liveChatSettings[bot.id];
          // Show a single spinner while either the agent_status or the
          // per-bot roster is still loading so the card doesn't pop in
          // piece by piece.
          const rosterReady = rosterLoadedBots.has(bot.id);
          const isInitialLoading = agentStatusLoading || !rosterReady;
          if (isInitialLoading) {
            return (
              <div key={bot.id} className="live-inbox-availability is-loading">
                <div className="live-inbox-availability-header">
                  <div className="live-inbox-availability-title-block">
                    <span className="live-inbox-availability-title live-inbox-card-title">
                      <span>{t('live_inbox_availability_title') || 'Availability'}</span>
                      <InfoTooltip text={t('live_inbox_availability_tooltip') || 'Live agents who are online can be requested by visitors directly from the chat. You can set yourself online or offline at any time, and admins can take a teammate offline if needed.'} />
                    </span>
                  </div>
                  <span className="live-inbox-availability-loading" aria-label="Loading">
                    <FontAwesomeIcon icon={faSpinner} spin />
                  </span>
                </div>
              </div>
            );
          }
          const mode = settings?.availability_mode || 'manual';
          const rosterRaw = teamAgents[bot.id] || [];
          // Make sure self always appears, even before the roster has
          // loaded (fallback to the locally-known online status).
          const hasSelf = rosterRaw.some(a => a.is_self);
          const roster = hasSelf ? rosterRaw : [
            {
              user_id: Number(localStorage.getItem('user_id')) || 0,
              first_name: localStorage.getItem('first_name') || '',
              last_name: '',
              email: '',
              role: localStorage.getItem('role') || 'owner',
              online: !!onlineStatus[bot.id],
              is_self: true,
            },
            ...rosterRaw,
          ];
          // Self always on top, then by online desc, then name.
          const sortedRoster = [...roster].sort((a, b) => {
            if (a.is_self !== b.is_self) return a.is_self ? -1 : 1;
            if (a.online !== b.online) return a.online ? -1 : 1;
            const an = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email || '';
            const bn = `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.email || '';
            return an.localeCompare(bn);
          });
          const onlineCount = sortedRoster.filter(a => a.online).length;
          const selfAgent = sortedRoster.find(a => a.is_self);
          const otherAgents = sortedRoster.filter(a => !a.is_self);
          const renderAgentRow = (a) => {
            const displayName = (a.first_name || a.last_name)
              ? `${a.first_name || ''} ${a.last_name || ''}`.trim()
              : (a.email || `User #${a.user_id}`);
            const key = `${bot.id}|${a.user_id}`;
            const isSelf = a.is_self;
            const canForceOff = !isSelf && a.online && canWrite();
            const interactive = isSelf || canForceOff;
            const busy = isSelf
              ? !!togglingStatus[bot.id]
              : !!forcingOffline[key];
            const onClick = () => {
              if (!interactive || busy) return;
              if (isSelf) {
                handleToggleOnline(bot.id);
              } else {
                setPendingForceOffline({
                  botId: bot.id,
                  userId: a.user_id,
                  name: displayName,
                });
              }
            };
            return (
              <li key={a.user_id} className="live-inbox-agent-row">
                <span className={`live-inbox-agent-dot ${a.online ? 'online' : 'offline'}`} />
                <span className="live-inbox-agent-name" title={a.email || ''}>
                  {displayName}
                </span>
                <Tooltip
                  title={
                    interactive
                      ? (a.online
                          ? (t('live_inbox_team_set_offline') || 'Set offline')
                          : (t('live_inbox_team_set_online') || 'Set online'))
                      : (isSelf
                          ? ''
                          : (t('live_inbox_team_toggle_disabled') || 'Only this teammate can set themselves online.'))
                  }
                  arrow
                  placement="top"
                  enterDelay={300}
                  enterNextDelay={150}
                  disableInteractive
                >
                  <span className="live-inbox-toggle-wrap">
                    <button
                      className={`live-inbox-toggle ${a.online ? 'online' : 'offline'}${interactive ? '' : ' is-disabled'}`}
                      onClick={onClick}
                      disabled={!interactive || busy}
                      aria-disabled={!interactive || busy}
                    >
                      {busy ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon icon={a.online ? faToggleOn : faToggleOff} />
                      )}
                      <span>{a.online
                        ? (t('live_inbox_online') || 'Online')
                        : (t('live_inbox_offline') || 'Offline')
                      }</span>
                    </button>
                  </span>
                </Tooltip>
              </li>
            );
          };
          return (
            <div key={bot.id} className="live-inbox-availability">
              <div className="live-inbox-availability-header">
                <div className="live-inbox-availability-title-block">
                  <span className="live-inbox-availability-title live-inbox-card-title">
                    <span>{t('live_inbox_availability_title') || 'Availability'}</span>
                    <InfoTooltip text={t('live_inbox_availability_tooltip') || 'Live agents who are online can be requested by visitors directly from the chat. You can set yourself online or offline at any time, and admins can take a teammate offline if needed.'} />
                  </span>
                  <span className="live-inbox-availability-desc">
                    {onlineCount > 0
                      ? (t('live_inbox_availability_desc_some_online') || 'At least one agent is online — visitors can request a human agent.')
                      : (t('live_inbox_availability_desc_all_offline') || 'All agents are offline — visitors cannot request a human agent.')
                    }
                  </span>
                </div>
                <span className="live-inbox-availability-count">
                  {onlineCount}/{sortedRoster.length} {t('live_inbox_online') || 'online'}
                </span>
              </div>

              {selfAgent && (
                <div className="live-inbox-agent-section">
                  <div className="live-inbox-agent-section-header">
                    {t('live_inbox_my_availability') || 'My availability'}
                  </div>
                  <ul className="live-inbox-agent-list">
                    {renderAgentRow(selfAgent)}
                  </ul>
                </div>
              )}

              <div className="live-inbox-agent-section-divider" />

              <div className="live-inbox-agent-section">
                <div className="live-inbox-agent-section-header">
                  {t('live_inbox_other_agents') || 'Other agents'}
                </div>
                {otherAgents.length === 0 ? (
                  <div className="live-inbox-agent-empty-block">
                    <div className="live-inbox-agent-empty">
                      {t('live_inbox_no_other_agents') || 'No other agents in this organization yet.'}
                    </div>
                    {canWrite() && (
                      <button
                        type="button"
                        className="live-inbox-invite-btn"
                        onClick={() => navigate('/team-members')}
                      >
                        <FontAwesomeIcon icon={faUserPlus} />
                        <span>{t('live_inbox_invite_team_members') || 'Invite team members'}</span>
                      </button>
                    )}
                  </div>
                ) : (
                  <ul className={`live-inbox-agent-list${otherAgents.length > 3 ? ' is-scrollable' : ''}`}>
                    {otherAgents.map(renderAgentRow)}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
        {hasEnabledBots && (
          <div className="live-inbox-bot-status">
            <div className="live-inbox-bot-info">
              <span className="live-inbox-bot-name live-inbox-card-title">
                <span>{t('live_inbox_notifications_title') || 'Notifications'}</span>
                <InfoTooltip text={t('live_inbox_notifications_tooltip') || 'Choose how you want to be notified about new visitor conversations, agent requests, and other live chat events.'} />
              </span>
              <span className="live-inbox-bot-desc">
                {t('live_inbox_notifications_desc') || 'Manage how to get notified about new chats and more.'}
              </span>
            </div>
            <button
              className="live-inbox-toggle online"
              onClick={() => {
                const botId = selectedBotIds[0] || localStorage.getItem('selectedBotId') || '';
                const qs = botId
                  ? `?tab=notifications&botId=${encodeURIComponent(botId)}`
                  : '?tab=notifications';
                navigate(`/settings${qs}`);
              }}
            >
              <FontAwesomeIcon icon={faBell} />
              <span>{t('live_inbox_manage_notifications') || 'Manage Notifications'}</span>
            </button>
          </div>
        )}
      </div>
      )}

      {/* Active conversations list */}
      <div className="live-inbox-conversations">
        <div className="live-inbox-conversations-header">
          <span className="live-inbox-card-title">
            {t('live_inbox_current_conversations') || 'Current Conversations'}
          </span>
        </div>
        {loadingSessions ? (
          <div className="live-inbox-loading">
            <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#5616ea', fontSize: '20px' }} />
          </div>
        ) : activeConversations.length === 0 ? (
          <div className="live-inbox-no-conversations">
            <FontAwesomeIcon icon={faUser} className="live-inbox-no-conversations-icon" />
            <h3>{t('live_inbox_no_visitors_title') || 'No active visitors'}</h3>
            <p>
              {!hasEnabledBots
                ? (t('live_inbox_no_visitors_default') || 'Active conversations will appear here.')
                : Object.values(onlineStatus).some(Boolean)
                  ? (t('live_inbox_no_visitors_online') || 'You\'re online. Visitors can request an agent to join. Active conversations will appear here.')
                  : (t('live_inbox_no_visitors_offline') || 'Active conversations will appear here. Go online to allow visitors to request an agent.')
              }
            </p>
          </div>
        ) : (
          <div className="live-inbox-conversation-list">
            {activeConversations.map((conversation, index) => {
              // Task 5 — presence dot for each conversation item
              const itemPresence = getVisitorPresenceStatus(conversation.lastActivity, conversation.visibility);
              const handleItemOpen = () => {
                if (joiningSession) return;
                if (conversation.agentJoined) {
                  handleOpenConversation(conversation.sessionId);
                } else {
                  handleViewConversation(conversation.sessionId);
                }
              };
              return (
                <div
                  key={conversation.sessionId}
                  className="live-inbox-conversation-item"
                  role="button"
                  tabIndex={0}
                  onClick={handleItemOpen}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleItemOpen();
                    }
                  }}
                >
                  <span className="live-inbox-conversation-index">#{index + 1}</span>
                  {/* Task 5 — avatar with presence dot */}
                  <div className="live-inbox-conversation-avatar">
                    <FontAwesomeIcon icon={faUser} />
                    <span
                      className={`status-dot avatar-presence ${itemPresence}`}
                      title={itemPresence === 'background' ? t('live_inbox_presence_background_hint') : undefined}
                    ></span>
                  </div>
                  <div className="live-inbox-conversation-info">
                    <span className={`live-inbox-conversation-name${conversation.unreadCount > 0 ? ' has-unread' : ''}`}>
                      {conversation.visitorName || t('live_inbox_visitor')}
                      <span
                        className="live-inbox-conversation-country"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '12px', marginLeft: '8px', verticalAlign: 'middle' }}
                        title={conversation.country || ''}
                      >
                        {conversation.country ? (
                          <img
                            src={getCountryFlagUrl(conversation.country)}
                            alt={conversation.country}
                            className="chat-item-country-flag"
                          />
                        ) : countryUnresolved.has(conversation.sessionId) ? null : (
                          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '10px', color: '#999' }} />
                        )}
                      </span>
                      {conversation.agentJoined && (
                        <span className="live-inbox-joined-badge">
                          <FontAwesomeIcon icon={faCheck} />
                          {t('live_inbox_agent_joined')}
                        </span>
                      )}
                      {conversation.requestedHelp && (
                        <span className="live-inbox-help-badge">
                          <FontAwesomeIcon icon={faTriangleExclamation} />
                          {t('live_inbox_help_requested')}
                        </span>
                      )}
                    </span>
                    <div className="live-inbox-conversation-preview">
                      {conversation.lastMessage ? (
                        <ReactMarkdown
                          components={InlinePreviewComponents}
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                        >
                          {conversation.lastMessage}
                        </ReactMarkdown>
                      ) : (
                        t('live_inbox_waiting')
                      )}
                    </div>
                  </div>
                  <div className="live-inbox-conversation-right">
                    <span className="live-inbox-conversation-time">
                      {conversation.unreadCount > 0 && (
                        <span className="live-inbox-unread-badge">{conversation.unreadCount}</span>
                      )}
                      {dayjs.utc(conversation.lastActivity).tz(getUserTimezone()).format('HH:mm')}
                    </span>
                  </div>
                  <div className="live-inbox-conversation-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="live-inbox-view-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (conversation.agentJoined) {
                          handleOpenConversation(conversation.sessionId);
                        } else {
                          handleViewConversation(conversation.sessionId);
                        }
                      }}
                      disabled={joiningSession}
                    >
                      <FontAwesomeIcon icon={faEye} />
                      <span>{conversation.agentJoined
                        ? (t('live_inbox_open') || 'Open')
                        : (t('live_inbox_view') || 'View')
                      }</span>
                    </button>
                    {conversation.agentJoined ? (
                      <button
                        className="live-inbox-leave-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLeaveConfirm({ sessionId: conversation.sessionId, visitorName: conversation.visitorName || '' });
                        }}
                        disabled={leavingSessionId === conversation.sessionId}
                      >
                        {leavingSessionId === conversation.sessionId ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          <FontAwesomeIcon icon={faArrowRightFromBracket} />
                        )}
                        <span>{t('live_inbox_leave')}</span>
                      </button>
                    ) : (
                      <button
                        className="live-inbox-join-btn"
                        onClick={(e) => { e.stopPropagation(); requestJoin(conversation.sessionId); }}
                        disabled={joiningSession}
                      >
                        {joiningSession ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          <FontAwesomeIcon icon={faArrowRightToBracket} />
                        )}
                        <span>{t('live_inbox_join')}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <JoinConversationModal
        open={joinModalOpen}
        onClose={() => setJoinModalOpen(false)}
        onConfirm={(agentName) => {
          setJoinModalOpen(false);
          if (joinModalSessionId) {
            handleJoinConversation(joinModalSessionId, agentName);
          }
        }}
        defaultAgentName={
          (() => {
            const conv = activeConversations.find(c => c.sessionId === joinModalSessionId);
            return conv ? (liveChatSettings[conv.botId]?.agent_display_name || '') : '';
          })()
        }
      />
      <SessionEndedModal
        open={!!sessionEndedModal}
        visitorName={sessionEndedModal?.visitorName || ''}
        reason={sessionEndedModal?.reason || 'ended'}
        onClose={() => setSessionEndedModal(null)}
        t={t}
      />
      <LeaveConversationModal
        open={!!leaveConfirm}
        visitorName={leaveConfirm?.visitorName || ''}
        onClose={() => setLeaveConfirm(null)}
        onConfirm={() => {
          const sid = leaveConfirm?.sessionId;
          setLeaveConfirm(null);
          if (sid) handleLeaveConversation(sid);
        }}
      />
      <PoweredByUpgradeModal
        open={!!assistUpgradeModal}
        bot={assistUpgradeModal?.bot}
        title={assistUpgradeModal?.title}
        description={assistUpgradeModal?.description}
        requiredPlan={assistUpgradeModal?.requiredPlan}
        onClose={() => setAssistUpgradeModal(null)}
        onUpgrade={() => {
          const b = assistUpgradeModal?.bot;
          setAssistUpgradeModal(null);
          if (b) upgradeClick(b);
        }}
      />
      {AssistSubscriptionModal}
      <WarningModal
        open={!!pendingForceOffline}
        onClose={() => setPendingForceOffline(null)}
        title={t('force_offline_title') || 'Set teammate offline?'}
        cancelText={t('cancel') || 'Cancel'}
        confirmText={t('force_offline_confirm') || 'Set offline'}
        danger
        onConfirm={() => {
          if (!pendingForceOffline) return;
          const { botId, userId } = pendingForceOffline;
          setPendingForceOffline(null);
          handleForceTeammateOffline(botId, userId);
        }}
      >
        {pendingForceOffline && (
          <>
            {t('force_offline_body_prefix') || 'Are you sure you want to set'}{' '}
            <strong>{pendingForceOffline.name}</strong>{' '}
            {t('force_offline_body_suffix') || 'offline?'}
            <br /><br />
            {t('force_offline_warning_prefix') || 'Note: only the teammate themselves can '}
            <strong>{t('force_offline_warning_bold') || 'set their status back to online'}</strong>
            {t('force_offline_warning_suffix') || ' from their own account.'}
          </>
        )}
      </WarningModal>
      <WarningModal
        open={!!deepLinkNotice}
        onClose={() => setDeepLinkNotice(null)}
        title={t('live_deeplink_ended_title')}
        buttonText={t('warning_got_it')}
      >
        {t('live_deeplink_ended_body')}
      </WarningModal>
    </div>
  );
};

// Modal that previews an outgoing reply translated into the visitor's
// language. The agent can edit the translation in-place before confirming.
// (Removed: outgoing translation now happens in-place inside the textarea
// via the per-input flag picker next to the send button.)

// Small modal informing the agent that the visitor ended the session
// (cleared chat / closed widget). Triggered by the `live_session_ended`
// WS event when the agent was viewing/joined/connecting to that session.
const SessionEndedModal = ({ open, visitorName, reason = 'ended', onClose, t }) => {
  const displayName = visitorName || (t('live_inbox_visitor') || 'The visitor');
  const isDisconnect = reason === 'disconnected';
  const titleText = isDisconnect
    ? (t('live_inbox_agent_disconnected_title') || 'Visitor disconnected the agent')
    : (t('live_inbox_session_ended_title') || 'Conversation closed');
  const descTemplate = isDisconnect
    ? (t('live_inbox_agent_disconnected_desc') || '{{name}} disconnected the live agent. The AI assistant has taken over the conversation.')
    : (t('live_inbox_session_ended_desc') || '{{name}} has closed this conversation. Any further messages will start a new session.');
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: 4,
          width: '90%',
          maxWidth: 380,
          outline: 'none',
        }}
      >
        <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
          {titleText}
        </h2>
        <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
          {descTemplate.replace('{{name}}', displayName)}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: '#5616ea',
              '&:hover': { backgroundColor: '#4a14d6' },
            }}
          >
            {t('ok') || 'OK'}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default LiveInbox;
